import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { format, parseISO } from 'date-fns';
import {
  Plus,
  Send,
  Calendar,
  ChevronDown,
  Trash2,
  Pencil,
  Info,
  Wand2,
  ArrowLeftRight,
  Thermometer,
  CheckCircle,
  Clock,
  FileCheck,
  RefreshCw,
  X as XIcon,
  AlertTriangle,
  ArrowRight,
  UserPlus,
  Undo2,
  Pin,
  History,
} from 'lucide-react';
import toast from 'react-hot-toast';

import api from '../api/axios';
import SickNoteToggle from '../components/common/SickNoteToggle';
import Modal from '../components/common/Modal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { addDaysStr, formatVacationRange } from '../constants/vacationRange';
import { PersonSummaryCard, TeamOverviewCard } from '../components/roster/PersonSummary';
import { FillResultModal, SlotCandidates, UNFILLED_REASON, BLOCK_REASON, maxDaysReason } from '../components/roster/FillTools';
import { GeneratorTopics, DefaultLevelActions } from '../components/roster/GeneratorLevels';
import { QualitySummary } from '../components/roster/QualitySummary';
import DayPeople from '../components/roster/DayPeople';
import DraftVersionsModal from '../components/roster/DraftVersions';
import PublishSnapshot from '../components/roster/PublishSnapshot';
import ShiftHistory from '../components/roster/ShiftHistory';
import { useUserColors } from '../context/UserColorsContext';
import { ColorDot } from '../components/common/PersonColor';
import { shiftLook, lookStyle, CHIP_STYLE, OPEN_LOOK } from '../components/roster/shiftLook';
import { MOBILE_VIEWS, readMobileView, saveMobileView } from '../components/roster/mobileView';
import WhistleIcon, { TrainingChip, TRAINING_LABEL } from '../components/common/WhistleIcon';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPeriodLabel(period) {
  if (!period) return '';
  const toMonthYear = (d) =>
    new Date(d + 'T00:00:00Z').toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const start = toMonthYear(period.start_date);
  const end = toMonthYear(period.end_date);
  return start === end ? start : `${start.split(' ')[0]}–${end}`;
}

function fmt(t) {
  return t ? t.slice(0, 5) : '';
}

// Legend samples (Indigo from the palette)
const LEGEND_SLOT = { assigned_user_id: 1, period_status: 'published' };
const LEGEND_COLOUR = '#4338CA';

function isOmMeeting(slot) {
  return slot?.event_participant_mode === 'all_om_role_holders';
}

// Open (unassigned) slot — white, dashed red border, in every view (shiftLook.js)
const OPEN_STYLE = OPEN_LOOK;
function isOpenSlot(slot) {
  return !!slot && !slot.assigned_user_id;
}
// Event icon kept on every card so the kind of slot stays clear
function slotTitle(slot, name = slot.template_name) {
  if (isOmMeeting(slot)) return '👥 OM-Meeting';
  if (slot.special_event_id) return `🧹 ${name || 'Event'}`;
  return name || 'Shift';
}

function OpenLabel({ size = 12 }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontWeight: 800, color: OPEN_STYLE.text }}>
      <AlertTriangle size={size} style={{ flexShrink: 0 }} aria-hidden="true" />
      OPEN
    </span>
  );
}

// The block itself (colour, stripes, border) is drawn by renderEventContent,
// so month (dot) and week events look the same; the FullCalendar frame stays transparent.
function slotToEvent(slot) {
  const classNames = ['fc-shift'];
  if (slot.period_status === 'draft') classNames.push('fc-event-draft');
  if (isOpenSlot(slot)) classNames.push('fc-event-open');

  return {
    id: String(slot.id),
    start: `${slot.date}T${slot.start_time}`,
    end: `${slot.date}T${slot.end_time}`,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    classNames,
    // Same start time: shift-type display order (Admin: Shift Templates), then longer first
    typeRank: slot.type_rank ?? 99,
    extendedProps: { slot },
  };
}

// Shifts of one day: start time, shift-type display order, longer first, name
const slotMins = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
function compareSlots(a, b) {
  if (a.start_time !== b.start_time) return a.start_time > b.start_time ? 1 : -1;
  const rank = (a.type_rank ?? 99) - (b.type_rank ?? 99);
  if (rank) return rank;
  const dur = (s) => (slotMins(s.end_time) - slotMins(s.start_time) + 1440) % 1440;
  return (dur(b) - dur(a)) || String(a.template_name || '').localeCompare(String(b.template_name || ''));
}

// "Training shift (trainer bonus)" line in shift dialogs
function TrainingLine() {
  return (
    <p style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '4px 0', fontSize: '0.85rem', color: 'var(--text)' }}>
      <WhistleIcon size={15} />
      {TRAINING_LABEL}
    </p>
  );
}

// Staff, someone else's / an open shift of a published plan:
//   open shift   → "Take this shift": the rules that apply are shown as
//                  information first (nothing blocks), then confirm
//   freed shift  → the existing cover flow (take / offer a swap)
function StaffTakeActions({ slot, cover, onTakeCover, onOfferSwap, onTookOpen, saving }) {
  const [notes, setNotes] = useState(null); // null = not asked yet
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNotes(null); }, [slot?.id]);

  const isOpen = slot.period_status === 'published' && !slot.assigned_user_id
    && slot.event_participant_mode !== 'all_om_role_holders';
  if (!isOpen && !cover) return null;

  const box = { padding: '12px', borderRadius: 'var(--radius)', background: 'var(--primary-xlight)', border: '1px solid var(--primary)' };

  if (cover) {
    return (
      <div style={{ ...box, display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ flex: '1 1 180px', fontSize: '0.88rem' }}>
          {cover.cover_type === 'swap' ? `${cover.original_user_name?.split(' ')[0] || 'Someone'} is looking for a swap.` : 'This shift needs someone on short notice.'}
        </span>
        <button className="btn btn-primary btn-sm" style={{ minHeight: '40px' }} disabled={saving} onClick={() => onTakeCover(cover.id)}>
          <CheckCircle size={14} /> Take this shift
        </button>
        {cover.cover_type === 'swap' && (
          <button className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }} disabled={saving} onClick={() => onOfferSwap(cover)}>
            <ArrowLeftRight size={14} /> Offer to swap
          </button>
        )}
      </div>
    );
  }

  const check = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/shifts/slots/${slot.id}/take-open`);
      setNotes(r.data.notes || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not check this shift.');
    } finally {
      setBusy(false);
    }
  };
  const take = async () => {
    setBusy(true);
    try {
      await api.post(`/shifts/slots/${slot.id}/take-open`, {});
      toast.success('The shift is yours.');
      onTookOpen?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not take this shift.');
      setNotes(null);
    } finally {
      setBusy(false);
    }
  };

  if (notes === null) {
    return (
      <div style={{ ...box, display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ flex: '1 1 180px', fontSize: '0.88rem' }}>This shift is open – first come, first served.</span>
        <button className="btn btn-primary btn-sm" style={{ minHeight: '40px' }} disabled={busy} onClick={check}>
          <CheckCircle size={14} /> {busy ? 'Checking…' : 'Take this shift'}
        </button>
      </div>
    );
  }
  return (
    <div role="alertdialog" aria-label="Take this shift" style={box}>
      {notes.length ? (
        <>
          <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: '0.88rem' }}>Please note – you can still take it, the admins will be told:</p>
          <ul style={{ margin: '0 0 10px', paddingLeft: '18px', fontSize: '0.85rem', lineHeight: 1.5 }}>
            {notes.map((n) => <li key={n}>{n}</li>)}
          </ul>
        </>
      ) : (
        <p style={{ margin: '0 0 10px', fontSize: '0.88rem' }}>No rule concerns. Take this shift?</p>
      )}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-sm" style={{ minHeight: '40px' }} disabled={busy} onClick={take}>
          {busy ? 'Taking…' : 'Confirm – take this shift'}
        </button>
        <button className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }} disabled={busy} onClick={() => setNotes(null)}>Cancel</button>
      </div>
    </div>
  );
}

// ── PeriodBadge ───────────────────────────────────────────────────────────────
function PeriodBadge({ status }) {
  return (
    <span className={`badge ${status === 'published' ? 'badge-success' : 'badge-warning'}`}>
      {status === 'published' ? 'Published' : 'Draft'}
    </span>
  );
}

// ── Confirmable server warnings (409) ───────────────────────────────────────
// Cap, qualification and double-shift warnings ask for confirmation and are
// re-sent with the matching flag; flags accumulate over several warnings.
// Returns the extra flags, false when declined, or null when it isn't a warning.
function confirmServerWarning(err, { verb = 'Assign', self = false, notifiesAdmins = false } = {}) {
  const d = err?.response?.status === 409 ? err.response.data : null;
  if (!d) return null;
  if (d.cap_warning) {
    const ci = d.cap_info;
    const lines = [];
    if (ci.reasons.includes('emergency_max')) {
      lines.push(`${ci.user_name} would get ${ci.shifts} regular shifts in ${ci.month} (emergency max ${ci.emergency_max}).`);
    }
    if (ci.reasons.includes('minijob_cap')) {
      lines.push(`${ci.user_name} would have ${ci.hours} h in ${ci.month}, above the minijob cap of ${ci.cap_hours} h (events included).`);
    }
    return window.confirm(`${lines.join('\n')}\n${verb} anyway?`) ? { ack_cap: true } : false;
  }
  if (d.qualification_warning) {
    // Manual assignment: "[Name] is not qualified for Tagesleitung. Assign anyway?"
    // Covers taken / confirmed by users: allowed too, but the admins are told.
    const lines = self
      ? [`You are not qualified for ${d.qualification_info.label}.`]
      : (d.qualification_all || [d.qualification_info]).map((q) => `${q.user_name} is not qualified for ${q.label}.`);
    if (notifiesAdmins) lines.push('The admins will be notified.');
    // Admin edits always ask "Assign anyway?" (also from the Save button)
    const qVerb = self || notifiesAdmins ? verb : 'Assign';
    return window.confirm(`${lines.join('\n')}\n${qVerb} anyway?`) ? { ack_qualification: true } : false;
  }
  if (d.legal_warning && d.legal_lines?.length) {
    // Swaps / cover requests (staff): plain sentences from the server, the
    // admins are told when the staff member goes ahead
    return window.confirm(`${d.legal_lines.join('\n')}\nAdmins will be informed.\n\n${verb} anyway?`) ? { ack_legal: true } : false;
  }
  if (d.legal_warning) {
    // "Anna would work 11.5 h on Mon 10 Nov (max 10 h). Assign anyway?"
    const li = d.legal_info;
    const day = (x) => format(parseISO(x), 'EEE d MMM');
    const lines = [];
    if (li.reasons.includes('daily_hours')) lines.push(`${li.user_name} would work ${li.day_hours} h on ${day(li.date)} (max ${li.max_day_hours} h).`);
    if (li.reasons.includes('rest_before')) lines.push(`${li.user_name} would have only ${li.rest_before_hours} h rest after the shift on ${day(li.prev_date)} (min ${li.min_rest_hours} h).`);
    if (li.reasons.includes('rest_after')) lines.push(`${li.user_name} would have only ${li.rest_after_hours} h rest before the shift on ${day(li.next_date)} (min ${li.min_rest_hours} h).`);
    return window.confirm(`${lines.join('\n')}\nThis breaks a legal working-time limit. ${verb} anyway?`) ? { ack_legal: true } : false;
  }
  if (d.streak_warning) {
    // "Anna would work 6 days in a row (Wed 3 Jun – Mon 8 Jun), max 5. Assign anyway?"
    const si = d.streak_info;
    const day = (x) => format(parseISO(x), 'EEE d MMM');
    return window.confirm(
      `${si.user_name} would work ${si.days} days in a row (${day(si.from)} – ${day(si.to)}), max ${si.max}.\n`
      + `This goes over the absolute max days in a row. ${verb} anyway?`,
    ) ? { ack_streak: true } : false;
  }
  if (d.conflict) {
    const ci = d.conflict_info;
    const who = self ? 'You already work' : `${ci.user_name || 'This person'} already works`;
    return window.confirm(`${who}${ci.shift_time ? ` the ${ci.shift_time} shift` : ' a shift'} on this day. ${verb} anyway?`)
      ? { force: true } : false;
  }
  return null;
}

// ── Event content ─────────────────────────────────────────────────────────────
// The whole block in the assignee's colour (published), a striped pale tint
// (draft), or white with a dashed red border (open) — see shiftLook.js.
function makeEventContent(colours) {
  return function renderEventContent({ event }) {
    const { slot } = event.extendedProps;
    if (!slot) return null;
    const open = isOpenSlot(slot);
    const look = shiftLook(slot, colours?.[slot.assigned_user_id]);
    const line = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

    return (
      <div
        className="fc-shift-block"
        style={{
          ...lookStyle(look),
          padding: open ? '0 2px' : '1px 4px', overflow: 'hidden', lineHeight: 1.3, width: '100%', height: '100%', position: 'relative',
          boxSizing: 'border-box', borderRadius: '4px',
        }}
      >
        {/* Open: "OPEN" first and without the icon, so it fits a narrow month cell on a phone */}
        {open && (
          <div style={{ ...line, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.02em' }}>OPEN</div>
        )}
        {/* Whistle as a corner badge, so the name still fits a narrow month cell */}
        {slot.is_training ? (
          <TrainingChip size={9} style={{ position: 'absolute', top: '1px', right: '1px', padding: '1px', zIndex: 1 }} />
        ) : null}
        <div style={{ ...line, fontWeight: 600, fontSize: '0.72rem' }}>
          {slotTitle(slot)}
        </div>
        {!open && (
          <div style={{ ...line, fontSize: '0.68rem', fontWeight: 500 }}>
            {slot.is_pinned ? <span style={{ ...CHIP_STYLE, padding: '0 2px', marginRight: '2px' }} title="Pinned — kept when generating again" aria-label="Pinned">📌</span> : null}
            {slot.assigned_user_name}
          </div>
        )}
        {slot.period_status === 'draft' && (
          <div style={{ fontSize: '0.62rem', fontStyle: 'italic' }}>Draft</div>
        )}
        {slot.pending_cover_type && (
          <div style={{ fontSize: '0.62rem', fontWeight: 600 }}>
            <span style={CHIP_STYLE}>{slot.pending_cover_type === 'sick_cover' ? '🏥 Cover needed' : '↔ Swap wanted'}</span>
          </div>
        )}
      </div>
    );
  };
}

// Vacations + absences per date → { vacation: [{user_id, name}], absent: [...] }
function buildPeopleByDate(vacations, absences, onlyUserId) {
  const out = {};
  const add = (date, key, person) => {
    const e = (out[date] = out[date] || { vacation: [], absent: [] });
    if (!e[key].some((p) => p.user_id === person.user_id)) e[key].push(person);
  };
  for (const v of vacations) {
    if (onlyUserId && v.user_id !== onlyUserId) continue;
    for (let ds = v.start_date; ds <= v.end_date; ds = addDaysStr(ds, 1)) add(ds, 'vacation', { user_id: v.user_id, name: v.user_name });
  }
  for (const a of absences) {
    if (onlyUserId && a.user_id !== onlyUserId) continue;
    for (let ds = a.start_date; ds <= a.end_date; ds = addDaysStr(ds, 1)) {
      add(ds, 'absent', { user_id: a.user_id, name: a.user_name, absence_type: a.absence_type });
    }
  }
  for (const e of Object.values(out)) {
    e.vacation.sort((x, y) => x.name.localeCompare(y.name));
    e.absent.sort((x, y) => x.name.localeCompare(y.name));
  }
  return out;
}

// ── CreatePeriodModal ─────────────────────────────────────────────────────────
function CreatePeriodModal({ isOpen, onClose, onSave, saving }) {
  const [form, setForm] = useState({ start_date: '', end_date: '' });

  useEffect(() => {
    if (!isOpen) setForm({ start_date: '', end_date: '' });
  }, [isOpen]);

  const set = (f) => (e) => setForm((prev) => ({ ...prev, [f]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.end_date < form.start_date) {
      toast.error('End date must be after start date.');
      return;
    }
    onSave(form);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Roster Plan" size="sm">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Start date</label>
          <input
            type="date"
            className="form-input"
            value={form.start_date}
            onChange={set('start_date')}
            required
            disabled={saving}
          />
        </div>
        <div className="form-group">
          <label className="form-label">End date</label>
          <input
            type="date"
            className="form-input"
            value={form.end_date}
            onChange={set('end_date')}
            required
            disabled={saving}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creating…' : (
              <><Plus size={15} /> Create Plan</>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── SlotModal ─────────────────────────────────────────────────────────────────
function SlotModal({
  isOpen, onClose, mode, slot, date, startTime, activePeriodId,
  periods = [], users = [], templates = [], onSave, onDelete, saving, isAdmin, onAssignCandidate, onTogglePin,
  openCover, onTakeCover, onOfferSwap, onTookOpen,
}) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && slot) {
      setForm({
        date: slot.date,
        start_time: fmt(slot.start_time),
        end_time: fmt(slot.end_time),
        shift_template_id: slot.shift_template_id ?? '',
        assigned_user_id: slot.assigned_user_id ?? '',
        notes: slot.notes ?? '',
        is_training: !!slot.is_training,
        roster_period_id: slot.roster_period_id,
      });
    } else {
      // Derive end_time: start + 8h or just +1h
      let endTime = '';
      if (startTime) {
        const [h, m] = startTime.split(':').map(Number);
        const endH = (h + 8) % 24;
        endTime = `${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
      setForm({
        date: date ?? '',
        start_time: startTime ?? '',
        end_time: endTime,
        shift_template_id: '',
        assigned_user_id: '',
        notes: '',
        is_training: false,
        roster_period_id: activePeriodId ?? '',
      });
    }
  }, [isOpen, mode, slot, date, startTime, activePeriodId]);

  const set = (f) => (e) => setForm((prev) => ({ ...prev, [f]: e.target.value }));

  // When template is selected, auto-fill times
  const handleTemplateChange = (e) => {
    const tid = e.target.value;
    setForm((prev) => ({ ...prev, shift_template_id: tid }));
    if (tid) {
      const t = templates.find((t) => String(t.id) === tid);
      if (t) setForm((prev) => ({ ...prev, shift_template_id: tid, start_time: t.start_time, end_time: t.end_time }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      date: form.date,
      start_time: form.start_time,
      end_time: form.end_time,
      shift_template_id: form.shift_template_id ? Number(form.shift_template_id) : null,
      assigned_user_id: form.assigned_user_id ? Number(form.assigned_user_id) : null,
      notes: form.notes || null,
      is_training: !!form.is_training,
    };
    onSave(payload, form.roster_period_id);
  };

  const handleDelete = () => {
    if (!window.confirm('Delete this shift? This cannot be undone.')) return;
    onDelete(slot.id);
  };

  // Read-only view for non-admins
  if (!isAdmin && mode === 'edit' && slot) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Shift Details" size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '2px' }}>Date</p>
            <p style={{ fontWeight: 500 }}>{slot.date ? format(parseISO(slot.date), 'EEE, d MMM yyyy') : '—'}</p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '2px' }}>Time</p>
            <p style={{ fontWeight: 500 }}>{fmt(slot.start_time)} – {fmt(slot.end_time)}</p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '2px' }}>Shift</p>
            <p style={{ fontWeight: 500 }}>{slot.template_name || 'Shift'}</p>
            {slot.is_training ? <TrainingLine /> : null}
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '2px' }}>Assigned to</p>
            <p style={{ fontWeight: 500 }}>{slot.assigned_user_name || 'Unassigned'}</p>
          </div>
          {slot.notes && (
            <div>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '2px' }}>Notes</p>
              <p style={{ fontSize: '0.9rem' }}>{slot.notes}</p>
            </div>
          )}
          <StaffTakeActions slot={slot} cover={openCover} onTakeCover={onTakeCover} onOfferSwap={onOfferSwap} onTookOpen={onTookOpen} saving={saving} />
          <ShiftHistory slot={slot} />
          <PeriodBadge status={slot.period_status} />
        </div>
      </Modal>
    );
  }

  const draftPeriods = periods.filter((p) => p.status === 'draft');
  const title = mode === 'create' ? 'New Shift' : 'Edit Shift';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      {mode === 'edit' && slot && !slot.assigned_user_id && onAssignCandidate && (
        <SlotCandidates slotId={slot.id} onAssign={(userId) => onAssignCandidate(slot, userId)} assigning={saving} />
      )}
      {mode === 'edit' && slot?.assigned_user_id && slot.period_status === 'draft' && onTogglePin && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px',
            padding: '10px 12px', borderRadius: 'var(--radius)', fontSize: '0.85rem',
            background: slot.is_pinned ? 'var(--primary-xlight)' : 'var(--bg)', border: '1px solid var(--border)',
          }}
        >
          <Pin size={15} aria-hidden="true" style={{ color: slot.is_pinned ? 'var(--primary-dark)' : 'var(--text-muted)' }} />
          <span style={{ flex: '1 1 200px' }}>
            {slot.is_pinned
              ? <><strong>Pinned</strong> — kept as it is when you generate again, and counted for everyone.</>
              : 'Not pinned — "Generate" may give this shift to someone else.'}
          </span>
          <button type="button" className="btn btn-secondary btn-sm" style={{ minHeight: '36px' }} disabled={saving}
            onClick={() => onTogglePin(slot, !slot.is_pinned)}>
            {slot.is_pinned ? 'Unpin' : 'Pin'}
          </button>
        </div>
      )}
      {mode === 'edit' && slot && <ShiftHistory slot={slot} boxed />}
      <form onSubmit={handleSubmit}>
        {/* Period selector (create mode only — edit is locked to original period) */}
        {mode === 'create' && (
          <div className="form-group">
            <label className="form-label">Roster period</label>
            {draftPeriods.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                No draft plans. Create a roster plan first.
              </p>
            ) : (
              <select
                className="form-select"
                value={form.roster_period_id}
                onChange={set('roster_period_id')}
                required
                disabled={saving}
              >
                <option value="">Select a plan…</option>
                {draftPeriods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.start_date} – {p.end_date}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-input"
              value={form.date}
              onChange={set('date')}
              required
              disabled={saving}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Shift template</label>
          <select
            className="form-select"
            value={form.shift_template_id}
            onChange={handleTemplateChange}
            disabled={saving}
          >
            <option value="">— None / custom —</option>
            {(templates || []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.start_time}–{t.end_time})
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Start time</label>
            <input
              type="time"
              className="form-input"
              value={form.start_time}
              onChange={set('start_time')}
              required
              disabled={saving}
            />
          </div>
          <div className="form-group">
            <label className="form-label">End time</label>
            <input
              type="time"
              className="form-input"
              value={form.end_time}
              onChange={set('end_time')}
              required
              disabled={saving}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Assign to</label>
          <select
            className="form-select"
            value={form.assigned_user_id}
            onChange={set('assigned_user_id')}
            disabled={saving}
          >
            <option value="">— Unassigned —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Notes</label>
          <input
            className="form-input"
            value={form.notes}
            onChange={set('notes')}
            placeholder="Optional notes…"
            disabled={saving}
          />
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minHeight: '36px', fontSize: '0.9rem' }}>
            <input
              type="checkbox"
              checked={!!form.is_training}
              onChange={(e) => setForm((prev) => ({ ...prev, is_training: e.target.checked }))}
              disabled={saving}
              style={{ width: '18px', height: '18px', flexShrink: 0 }}
            />
            <WhistleIcon size={16} />
            {TRAINING_LABEL}
          </label>
          <p style={{ margin: '2px 0 0 26px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Mark only the trainer&apos;s shift – not the trainee&apos;s.
          </p>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '20px',
          }}
        >
          {mode === 'edit' ? (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={handleDelete}
              disabled={saving}
            >
              <Trash2 size={14} />
              Delete
            </button>
          ) : (
            <div />
          )}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : mode === 'create' ? 'Add Shift' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// Reasons a pinned shift breaks a rule (besides the "who could take it" ones)
const PINNED_REASON = {
  inactive: 'person is no longer active',
  event_inactive: 'event is switched off',
  vacation: 'on approved vacation',
  absence: 'absent',
  not_qualified: 'not qualified for this shift type',
  daily_hours: 'more than 10 h that day',
  rest_period: 'less than 11 h rest',
  already_working: 'another shift that day',
  overlap: 'overlaps another shift',
  minijob_cap: 'above the minijob cap',
  weekend_rhythm: 'free weekend in their rhythm',
  blocked_day: 'marked this day as unavailable',
  max_streak: 'above the max days in a row',
};

// ── GenerationReportModal ─────────────────────────────────────────────────────
function GenerationReportModal({ report, onClose }) {
  const { summary = {}, unfilled = [], user_months = [], shift_blocks = [], cluster_settings, variety_settings = null, weekend_rhythms = null, emergency_assignments = [], fill_runs = [], warnings = [], tuning = null, tuning_rules = [], quality = null } = report;
  const maxInRow = variety_settings?.maxInRow ?? 2;
  const maxPerWeek = variety_settings?.maxPerWeek ?? 3;
  const pct = summary.total_slots > 0 ? summary.filled_slots / summary.total_slots : 1;
  const summaryColor = pct === 1 ? 'var(--success)' : pct >= 0.9 ? 'var(--warning)' : 'var(--danger)';

  const noSubWarnings = warnings.filter((w) => w.type === 'no_submission');
  const pairingWarnings = warnings.filter((w) => w.type === 'pairing_avoided');
  const eventPriorityWarnings = warnings.filter((w) => w.type === 'event_priority_holder_unavailable');
  const eventMinSlotsWarnings = warnings.filter((w) => w.type === 'event_min_slots_warning');
  const dailyHoursWarnings = warnings.filter((w) => w.type === 'daily_hours_exceeded');
  const restWarnings = warnings.filter((w) => w.type === 'rest_period_violated');
  const omUnavailableWarnings = warnings.filter((w) => w.type === 'om_meeting_holder_unavailable');
  const omOverrideWarnings = warnings.filter((w) => w.type === 'om_meeting_availability_override');
  const omLegalWarnings = warnings.filter((w) => w.type === 'om_meeting_legal_override');
  const omStreakWarnings = warnings.filter((w) => w.type === 'om_meeting_streak_override');
  const maxDaysWarnings = warnings.filter((w) => w.type === 'max_days_exceeded');
  const eventBlockedDayWarnings = warnings.filter((w) => w.type === 'event_blocked_day_override');
  const inactiveEventWarnings = warnings.filter((w) => w.type === 'event_inactive');
  const untypedWarnings = warnings.filter((w) => w.type === 'template_without_shift_type');
  const pendingVacationWarnings = warnings.filter((w) => w.type === 'pending_vacation');
  const noValuesWarnings = warnings.filter((w) => w.type === 'no_hours_values');
  const minijobMeetingWarnings = warnings.filter((w) => w.type === 'minijob_cap_exceeded_by_meeting');
  const qualificationWarnings = warnings.filter((w) => w.type === 'qualification_conflict');

  const cellStyle = { padding: '6px 10px' };
  const thStyle = { ...cellStyle, textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', background: 'var(--bg)' };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Generation Report"
      size="lg"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      }
    >
      {/* Pinned shifts that break a hard rule now — kept, never dropped */}
      {report.pinned_conflicts?.length > 0 && (
        <div
          role="alert"
          style={{
            padding: '10px 14px', marginBottom: '16px', borderRadius: 'var(--radius)', fontSize: '0.85rem',
            background: 'var(--danger-light)', border: '1px solid var(--danger)',
          }}
        >
          <p style={{ margin: '0 0 6px', fontWeight: 700, color: 'var(--danger)' }}>
            📌 {report.pinned_conflicts.length} pinned shift{report.pinned_conflicts.length === 1 ? '' : 's'} break{report.pinned_conflicts.length === 1 ? 's' : ''} a rule now — kept as pinned, please check
          </p>
          <ul style={{ margin: 0, paddingLeft: '18px', lineHeight: 1.5 }}>
            {report.pinned_conflicts.map((c) => (
              <li key={c.slot_id}>
                <strong>{c.user_name}</strong> · {c.label || 'Shift'} {format(parseISO(c.date), 'EEE d MMM')} {fmt(c.start_time)}–{fmt(c.end_time)}:{' '}
                {c.reasons.map((r) => PINNED_REASON[r] || BLOCK_REASON[r] || r).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}
      {report.pinned_count > 0 && !report.pinned_conflicts?.length && (
        <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: 'var(--text-light)' }}>
          📌 {report.pinned_count} pinned shift{report.pinned_count === 1 ? ' was' : 's were'} kept and counted for everyone.
        </p>
      )}

      {/* Draft quality summary + settings used */}
      {quality && <QualitySummary lines={quality} />}
      {tuning && (
        <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', margin: '-8px 0 16px' }}>
          Variant <strong>{tuning.variant}</strong> ·{' '}
          {(() => {
            const changed = tuning_rules.filter((r) => tuning.levels?.[r.key] && tuning.levels[r.key] !== 'normal');
            if (!changed.length) return 'all soft rules on Normal';
            const name = (k) => ({ off: 'Off', low: 'Low', normal: 'Normal', high: 'High', very_high: 'Very high' }[k] || k);
            return changed.map((r) => `${r.label}: ${name(tuning.levels[r.key])}`).join(' · ');
          })()}
          {tuning.defaults ? ' (defaults)' : ' (variant settings, defaults unchanged)'}
        </p>
      )}

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { label: 'Filled', value: summary.filled_slots, color: summaryColor },
          { label: 'Unfilled', value: summary.unfilled_slots, color: summary.unfilled_slots > 0 ? 'var(--danger)' : 'var(--text-muted)' },
          { label: 'Total', value: summary.total_slots, color: 'var(--text)' },
        ].map(({ label, value, color }) => (
          <div
            key={label}
            style={{
              flex: 1, minWidth: '90px', textAlign: 'center', padding: '12px 8px',
              borderRadius: 'var(--radius)', background: 'var(--bg)', border: '1px solid var(--border)',
            }}
          >
            <p style={{ fontSize: '1.8rem', fontWeight: 700, color }}>{value}</p>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Unfilled shifts */}
      {unfilled.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '8px', color: 'var(--danger)' }}>
            Unfilled shifts ({unfilled.length})
          </p>
          <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Shift</th>
                  <th style={thStyle}>Time</th>
                  <th style={thStyle}>Why open</th>
                </tr>
              </thead>
              <tbody>
                {unfilled.map((s, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={cellStyle}>{s.date}</td>
                    <td style={cellStyle}>{s.template_name || `${s.start_time}–${s.end_time}`}</td>
                    <td style={cellStyle}>{s.start_time.slice(0, 5)} – {s.end_time.slice(0, 5)}</td>
                    <td style={{ ...cellStyle, color: s.reason === 'legal_limits' ? 'var(--danger)' : 'var(--text-light)', fontWeight: s.reason === 'legal_limits' ? 600 : 400 }}>
                      {s.reason === 'max_streak' ? maxDaysReason(report.max_days_in_row) : s.reason ? (UNFILLED_REASON[s.reason] || s.reason) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* "Fill open shifts" runs after generation */}
      {fill_runs.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '6px' }}>Fill runs ({fill_runs.length})</p>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.82rem', color: 'var(--text-light)' }}>
            {fill_runs.map((r) => (
              <li key={r.id}>
                {new Date(r.at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}: filled {r.filled} of {r.total_open} open
                {r.undone_at && ` · undone (${r.undo_removed} removed${r.undo_skipped ? `, ${r.undo_skipped} kept` : ''})`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Emergency assignments */}
      {emergency_assignments.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '4px', color: 'var(--warning)' }}>
            Emergency assignments ({emergency_assignments.length})
          </p>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '8px' }}>
            These shifts would otherwise stay open and go beyond the person's monthly target (up to 110 %).
          </p>
          <div style={{ maxHeight: '160px', overflowY: 'auto', overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Shift</th>
                  <th style={thStyle}>Name</th>
                </tr>
              </thead>
              <tbody>
                {emergency_assignments.map((e, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={cellStyle}>{e.date}</td>
                    <td style={cellStyle}>{e.template_name || 'Shift'} · {e.start_time.slice(0, 5)}–{e.end_time.slice(0, 5)}</td>
                    <td style={cellStyle}>{e.user_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Shift blocks of people who want many shifts */}
      {shift_blocks.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '4px' }}>Shift blocks</p>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '8px' }}>
            People with {cluster_settings?.threshold ?? 4}+ shifts/week — regular shifts grouped on consecutive days
            (target max. {cluster_settings?.maxStreak ?? 5} in a row, across plan boundaries).
          </p>
          <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
            <table style={{ width: '100%', minWidth: '480px', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Month</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Longest streak</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Blocks</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Isolated days off</th>
                </tr>
              </thead>
              <tbody>
                {shift_blocks.map((b) => (
                  <tr key={`${b.user_id}-${b.month}`} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={cellStyle}>{b.name}</td>
                    <td style={cellStyle}>{b.month}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {b.longest_streak} {b.longest_streak === 1 ? 'day' : 'days'}
                      {b.over_max && (
                        <span className="badge badge-warning" style={{ fontSize: '0.7rem', marginLeft: '6px' }}>
                          longer than {cluster_settings?.maxStreak ?? 5} days
                        </span>
                      )}
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{b.blocks}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{b.isolated_days_off}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Weekend rhythm: new assignments + people per weekend */}
      {weekend_rhythms && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '4px' }}>Weekend rhythm</p>
          <p style={{ fontSize: '0.8rem', marginBottom: '6px' }}>
            {weekend_rhythms.newly_assigned?.length
              ? <>Newly assigned: {weekend_rhythms.newly_assigned.map((a) => `${a.name} → Rhythm ${a.rhythm}`).join(', ')}</>
              : 'No new assignments.'}
          </p>
          {weekend_rhythms.averages && (
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '8px' }}>
              Average available: A weekends {weekend_rhythms.averages.A ?? '–'} · B weekends {weekend_rhythms.averages.B ?? '–'}
            </p>
          )}
          {weekend_rhythms.weekends?.length > 0 && (
            <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
              <table style={{ width: '100%', minWidth: '420px', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Weekend</th>
                    <th style={thStyle}>Working rhythm</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Weekend slots</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Available people</th>
                  </tr>
                </thead>
                <tbody>
                  {weekend_rhythms.weekends.map((w) => (
                    <tr key={w.saturday} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{format(parseISO(w.saturday), 'EEE d MMM yyyy')}</td>
                      <td style={cellStyle}>Rhythm {w.side}</td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>{w.demand}</td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>{w.available_people}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Monthly targets per user */}
      {user_months.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '4px' }}>Monthly targets</p>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Regular shifts only — special events and Amt hours don't count. Rows below the contract minimum are highlighted.
            Variety: runs of more than {maxInRow} days with the same shift type, and weeks (Mon–Sun) with more than {maxPerWeek} of one type.
          </p>
          <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
            <table style={{ width: '100%', minWidth: '560px', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Month</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Min h</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Target</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Max</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Assigned</th>
                  <th style={thStyle}>Variety</th>
                  <th style={thStyle}>Flags</th>
                </tr>
              </thead>
              <tbody>
                {user_months.map((u) => (
                  <tr
                    key={`${u.user_id}-${u.month}`}
                    style={{ borderTop: '1px solid var(--border)', background: u.below_minimum ? 'var(--danger-light)' : undefined }}
                  >
                    <td style={cellStyle}>
                      {u.name}
                      {u.employment_type === 'minijob' && (
                        <span style={{ marginLeft: '4px', fontSize: '0.7rem', color: 'var(--text-muted)' }}>(Minijob)</span>
                      )}
                    </td>
                    <td style={cellStyle}>{u.month}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{u.min_hours > 0 ? `${u.min_hours} h` : '—'}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{u.target}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      {u.emergency_max}
                      {u.cap_hours != null && (
                        <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)' }}>cap {u.cap_hours} h</span>
                      )}
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {u.assigned_shifts} · {u.assigned_hours} h
                      {u.all_hours != null && u.all_hours !== u.assigned_hours && (
                        <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 400 }}>{u.all_hours} h incl. events</span>
                      )}
                    </td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                      {!u.variety || (u.variety.streaks_over === 0 && u.variety.weeks_over === 0) ? (
                        <span style={{ color: 'var(--success)' }} title="No same-type runs or weeks above the limits">✓ varied</span>
                      ) : (
                        <span style={{ color: '#E65100' }}>
                          {u.variety.streaks_over > 0 && (
                            <span style={{ display: 'block' }} title={`Runs of more than ${maxInRow} days with the same shift type`}>
                              {u.variety.streaks_over}× {maxInRow + 1}+ in a row
                            </span>
                          )}
                          {u.variety.weeks_over > 0 && (
                            <span style={{ display: 'block' }} title={`Weeks with more than ${maxPerWeek} shifts of one type`}>
                              {u.variety.weeks_over} {u.variety.weeks_over === 1 ? 'week' : 'weeks'} &gt; {maxPerWeek}/week
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td style={cellStyle}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {u.below_minimum && <span className="badge badge-danger" style={{ fontSize: '0.7rem' }}>below contract minimum</span>}
                        {u.emergency_used && <span className="badge badge-warning" style={{ fontSize: '0.7rem' }}>emergency range used</span>}
                        {u.minijob_cap_reached && <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>minijob cap reached</span>}
                        {u.no_values && <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>no contract/preference set</span>}
                        {u.no_submission && (
                          <span className="badge badge-warning" style={{ fontSize: '0.7rem' }}>
                            No submission{u.pass2_shifts > 0 ? ` · ${u.pass2_shifts} in pass 2` : ''}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '8px', color: 'var(--warning)' }}>
            Warnings ({warnings.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {noSubWarnings.map((w, i) => (
              <div
                key={`ns-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                <strong>{w.user_name}</strong> had no availability submission — assigned as fully available
              </div>
            ))}
            {noValuesWarnings.map((w, i) => (
              <div
                key={`nv-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                <strong>{w.user_name}</strong> has no contract hours and no preferred shifts — will not receive regular shifts — set values in the user panel
              </div>
            ))}
            {minijobMeetingWarnings.map((w, i) => (
              <div
                key={`mjm-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                👥 Minijob cap exceeded by OM-Meeting: <strong>{w.user_name}</strong> on {w.date} ({w.hours} h of {w.cap_hours} h cap)
              </div>
            ))}
            {qualificationWarnings.map((w, i) => (
              <div
                key={`qc-${i}`}
                style={{ padding: '8px 12px', background: 'var(--danger-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                🎓 <strong>{w.user_name}</strong> is assigned to {w.label} on {format(parseISO(w.date), 'EEE, d MMM')} ({w.start_time.slice(0, 5)}–{w.end_time.slice(0, 5)}) but is not qualified — not changed automatically
              </div>
            ))}
            {pendingVacationWarnings.map((w, i) => (
              <div
                key={`pv-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                🌴 Pending vacation request: <strong>{w.user_name}</strong>, {formatVacationRange(w.start_date, w.end_date)} — not blocked until approved
              </div>
            ))}
            {untypedWarnings.map((w, i) => (
              <div
                key={`ut-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                Template <strong>{w.template_name}</strong> has no shift type — preferences ignored for {w.slot_count} slot{w.slot_count !== 1 ? 's' : ''}
              </div>
            ))}
            {inactiveEventWarnings.map((w, i) => (
              <div
                key={`ie-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                Deactivated event <strong>{w.event_name}</strong> on {w.date}: {w.removed} slot{w.removed !== 1 ? 's' : ''} removed
                {w.kept > 0 && `, ${w.kept} kept (has time entries or sick records) and left unassigned`}
              </div>
            ))}
            {pairingWarnings.map((w, i) => (
              <div
                key={`pa-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                Avoid preference not met: <strong>{w.user_a}</strong> &amp; <strong>{w.user_b}</strong> both work on {w.date}
              </div>
            ))}
            {eventPriorityWarnings.map((w, i) => (
              <div
                key={`ep-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                ⚠ Priority role holder unavailable for <strong>{w.event_name}</strong> on {w.date}
              </div>
            ))}
            {eventMinSlotsWarnings.map((w, i) => (
              <div
                key={`em-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                ⚠ <strong>{w.event_name}</strong> on {w.date}: only {w.filled}/{w.required} slots filled
              </div>
            ))}
            {dailyHoursWarnings.map((w, i) => (
              <div
                key={`dh-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                ⏱ <strong>{w.user_name}</strong> – {w.date}: {w.hours} h scheduled (&gt;10 h)
              </div>
            ))}
            {restWarnings.map((w, i) => (
              <div
                key={`rp-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                🌙 <strong>{w.user_name}</strong> – {w.date1}/{w.date2}: only {w.rest_hours} h rest (&lt;11 h)
              </div>
            ))}
            {omUnavailableWarnings.map((w, i) => (
              <div
                key={`omu-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                👥 OM-Meeting {w.date}: <strong>{w.user_name}</strong> not assigned ({w.reason}) — slot left open
              </div>
            ))}
            {omOverrideWarnings.map((w, i) => (
              <div
                key={`omo-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                👥 OM-Meeting {w.date}: <strong>{w.user_name}</strong> assigned despite availability block ({w.reason})
              </div>
            ))}
            {maxDaysWarnings.map((w, i) => (
              <div
                key={`md-${i}`}
                style={{ padding: '8px 12px', background: 'var(--danger-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                📆 <strong>{w.user_name}</strong>: {w.days} days in a row ({format(parseISO(w.from), 'EEE d MMM')} – {format(parseISO(w.to), 'EEE d MMM')}),
                max {w.max} — please check
              </div>
            ))}
            {omStreakWarnings.map((w, i) => (
              <div
                key={`oms-${i}`}
                style={{ padding: '8px 12px', background: 'var(--danger-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                👥 OM-Meeting {w.date}: <strong>{w.user_name}</strong> assigned although it makes {w.days} days in a row (max {w.max}) — mandatory meeting, please check
              </div>
            ))}
            {omLegalWarnings.map((w, i) => (
              <div
                key={`oml-${i}`}
                style={{ padding: '8px 12px', background: 'var(--danger-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                👥 OM-Meeting {w.date}: <strong>{w.user_name}</strong> assigned although it breaks the{' '}
                {w.reason === 'daily_hours' ? '10 h day' : '11 h rest'} (shift in a neighbouring plan) — please check
              </div>
            ))}
            {eventBlockedDayWarnings.map((w, i) => (
              <div
                key={`ebd-${i}`}
                style={{ padding: '8px 12px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: '0.82rem' }}
              >
                🧹 {w.event_name} on {w.date}: <strong>{w.user_name}</strong> assigned despite blocked day
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── ClockModal (roster) ────────────────────────────────────────────────────────
function ClockModal({ slot, timeEntry, onClose, onSave, saving }) {
  if (!slot) return null;
  const existingIn = timeEntry?.clocked_in_at
    ? timeEntry.clocked_in_at.slice(-8, -3)
    : '';
  const existingOut = timeEntry?.clocked_out_at
    ? timeEntry.clocked_out_at.slice(-8, -3)
    : '';

  const [inTime, setInTime] = React.useState(existingIn || slot.start_time.slice(0, 5));
  const [outTime, setOutTime] = React.useState(existingOut || slot.end_time.slice(0, 5));

  const totalMins = (() => {
    if (!inTime || !outTime) return null;
    const [ih, im] = inTime.split(':').map(Number);
    const [oh, om] = outTime.split(':').map(Number);
    const diff = (oh * 60 + om) - (ih * 60 + im);
    return diff > 0 ? diff : null;
  })();

  return (
    <Modal isOpen onClose={onClose} title={timeEntry ? 'Update time entry' : 'Log work time'} size="sm">
      <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '16px' }}>
        <strong>{slot.template_name || 'Shift'}</strong> · {slot.date} · {fmt(slot.start_time)}–{fmt(slot.end_time)}
      </p>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Clock In</label>
          <input type="time" className="input" value={inTime} onChange={(e) => setInTime(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Clock Out</label>
          <input type="time" className="input" value={outTime} onChange={(e) => setOutTime(e.target.value)} />
        </div>
      </div>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '20px' }}>
        Total: <strong>{totalMins ? `${Math.floor(totalMins / 60)}h ${totalMins % 60}m` : '—'}</strong>
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
        <button
          className="btn btn-primary"
          onClick={() => onSave(slot, timeEntry, `${slot.date} ${inTime}:00`, outTime ? `${slot.date} ${outTime}:00` : null)}
          disabled={saving || !inTime}
        >
          {saving ? 'Saving…' : timeEntry ? 'Update' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

// ── SundayBonusModal ───────────────────────────────────────────────────────────
function SundayBonusModal({ data, onClose, onSave, onRemove, saving }) {
  if (!data) return null;
  const { dateStr, existing } = data;
  const [bonusType, setBonusType] = React.useState(existing?.bonus_type ?? 'standard_25');

  return (
    <Modal isOpen onClose={onClose} title="Sunday Bonus" size="sm">
      <p style={{ fontWeight: 600, marginBottom: '16px' }}>{dateStr}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
        {[
          { value: 'standard_25', label: 'High Revenue Sunday — 25%' },
          { value: 'high_50', label: 'Crazy Revenue Sunday — 50%' },
        ].map(({ value, label }) => (
          <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input
              type="radio"
              name="bonus_type"
              value={value}
              checked={bonusType === value}
              onChange={() => setBonusType(value)}
            />
            {label}
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
        {existing ? (
          <button className="btn btn-danger btn-sm" onClick={() => onRemove(existing.id)} disabled={saving}>
            Remove bonus
          </button>
        ) : <div />}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(dateStr, bonusType, existing?.id ?? null)} disabled={saving}>
            {saving ? 'Saving…' : existing ? 'Update' : 'Declare'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── ShiftActionModal ───────────────────────────────────────────────────────────
function ShiftActionModal({ slot, onClose, onSwap, onSick, onClock, timeEntry, sicknessRecord, onAu }) {
  if (!slot) return null;
  return (
    <Modal isOpen onClose={onClose} title="Your shift" size="sm">
      <p style={{ fontWeight: 600, marginBottom: '4px' }}>{slot.template_name || 'Shift'}</p>
      {slot.is_training ? <TrainingLine /> : null}
      <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '20px' }}>
        {slot.date} · {fmt(slot.start_time)} – {fmt(slot.end_time)}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <button
          className="btn btn-secondary"
          style={{ justifyContent: 'flex-start' }}
          onClick={() => { onClock(slot, timeEntry); onClose(); }}
        >
          <Clock size={15} />
          {timeEntry === undefined ? 'Loading…' : timeEntry ? 'Update clock times' : 'Clock In / Clock Out'}
        </button>
        {sicknessRecord && <SickNoteToggle record={sicknessRecord} onChange={onAu} />}
        <button
          className="btn btn-secondary"
          style={{ justifyContent: 'flex-start' }}
          onClick={() => { onSwap(slot); onClose(); }}
        >
          <ArrowLeftRight size={15} />
          I'd like to get rid of this shift
        </button>
        <button
          className="btn btn-danger"
          style={{ justifyContent: 'flex-start' }}
          onClick={() => { onSick(slot); onClose(); }}
        >
          <Thermometer size={15} />
          I'm sick and can't work this shift
        </button>
      </div>
      <div style={{ marginTop: '16px' }}>
        <ShiftHistory slot={slot} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

// ── CoverConfirmModal ──────────────────────────────────────────────────────────
function CoverConfirmModal({ data, onClose, onConfirm, saving }) {
  if (!data) return null;
  const { slot, actionType } = data;
  const isSick = actionType === 'sick';
  return (
    <Modal isOpen onClose={onClose} title={isSick ? 'Report sick leave' : 'Request shift cover'} size="sm">
      <p style={{ marginBottom: '8px' }}>
        <strong>{slot.template_name || 'Shift'}</strong> on <strong>{slot.date}</strong> ({fmt(slot.start_time)} – {fmt(slot.end_time)})
      </p>
      {isSick ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--danger)', background: 'var(--danger-light)', padding: '10px 12px', borderRadius: 'var(--radius)' }}>
          This sends an <strong>urgent notification</strong> to the entire team. The first person to respond will be assigned your shift.
        </p>
      ) : (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', background: 'var(--bg)', padding: '10px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
          This sends a low-priority notification to all team members. They can take the shift or offer a swap.
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
        <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
        <button
          className={`btn ${isSick ? 'btn-danger' : 'btn-primary'}`}
          onClick={() => onConfirm(slot, actionType)}
          disabled={saving}
        >
          {saving ? 'Submitting…' : isSick ? 'Report sick' : 'Request cover'}
        </button>
      </div>
    </Modal>
  );
}

// ── SwapPickerModal ────────────────────────────────────────────────────────────
function SwapPickerModal({ data, myShifts, onClose, onOffer, saving }) {
  if (!data) return null;
  const { cover } = data;
  return (
    <Modal isOpen onClose={onClose} title="Offer one of your shifts" size="md">
      <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '16px' }}>
        Select which of your shifts you want to offer in exchange for{' '}
        <strong>{cover.template_name || 'this shift'}</strong> on <strong>{cover.date}</strong>.
      </p>
      {myShifts.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>You have no upcoming shifts to offer.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '320px', overflowY: 'auto' }}>
          {myShifts.map((s) => (
            <div
              key={s.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 14px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                background: 'var(--white)',
              }}
            >
              <div>
                <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>{s.template_name || 'Shift'}</p>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
                  {s.date} · {fmt(s.start_time)} – {fmt(s.end_time)}
                </p>
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => onOffer(cover.id, s.id)}
                disabled={saving}
              >
                Offer this
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

// ── OpenCoversPanel ────────────────────────────────────────────────────────────
function OpenCoversPanel({ covers, onTake, onOfferSwap, saving }) {
  if (covers.length === 0) return null;
  return (
    <div className="card" style={{ marginTop: '20px' }}>
      <p style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ArrowLeftRight size={16} style={{ color: 'var(--primary-dark)' }} />
        Open Shift Cover Requests
        <span className="badge badge-warning" style={{ fontSize: '0.72rem' }}>{covers.length}</span>
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {covers.map((c) => (
          <div
            key={c.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px',
              padding: '10px 14px',
              borderRadius: 'var(--radius)',
              border: `1px solid ${c.cover_type === 'sick_cover' ? 'var(--danger)' : 'var(--warning)'}`,
              background: c.cover_type === 'sick_cover' ? 'var(--danger-light)' : 'var(--warning-light)',
            }}
          >
            <div>
              <p style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                {c.template_name || 'Shift'} · {c.date}
                {' '}
                <span className={`badge ${c.cover_type === 'sick_cover' ? 'badge-danger' : 'badge-warning'}`} style={{ fontSize: '0.7rem', marginLeft: '4px' }}>
                  {c.cover_type === 'sick_cover' ? '🏥 Sick cover' : '↔ Swap'}
                </span>
              </p>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
                {fmt(c.start_time)} – {fmt(c.end_time)} · {c.original_user_name}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => onTake(c.id)}
                disabled={saving}
              >
                <CheckCircle size={13} />
                Take this shift
              </button>
              {c.cover_type === 'swap' && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => onOfferSwap(c)}
                  disabled={saving}
                >
                  <ArrowLeftRight size={13} />
                  Offer to swap
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MyCoversPanel ──────────────────────────────────────────────────────────────
function MyCoversPanel({ myCovers, onConfirm, onCancel, saving }) {
  const { my_open = [], pending_confirm = [], my_offers = [] } = myCovers;
  if (my_open.length + pending_confirm.length + my_offers.length === 0) return null;

  const SectionHeader = ({ children }) => (
    <p style={{ fontWeight: 600, fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '12px 0 6px' }}>
      {children}
    </p>
  );

  const CoverRow = ({ c, actions }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px',
        padding: '8px 12px',
        borderRadius: 'var(--radius)',
        border: '1px solid var(--border)',
        background: 'var(--white)',
      }}
    >
      <div>
        <p style={{ fontWeight: 500, fontSize: '0.88rem' }}>{c.template_name || 'Shift'} · {c.date}</p>
        <p style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
          {fmt(c.start_time)} – {fmt(c.end_time)}
          {c.covering_user_name && ` · ${c.covering_user_name} offered`}
          {c.original_user_name && c.covering_user_id && ` · via ${c.original_user_name}`}
        </p>
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>{actions}</div>
    </div>
  );

  return (
    <div className="card" style={{ marginTop: '12px' }}>
      <p style={{ fontWeight: 700, fontSize: '0.95rem' }}>My Cover Activity</p>

      {my_open.length > 0 && (
        <>
          <SectionHeader>My open requests</SectionHeader>
          {my_open.map((c) => (
            <CoverRow key={c.id} c={c} actions={
              <button className="btn btn-secondary btn-sm" onClick={() => onCancel(c.id)} disabled={saving}>
                <XIcon size={12} /> Cancel
              </button>
            } />
          ))}
        </>
      )}

      {pending_confirm.length > 0 && (
        <>
          <SectionHeader>Pending your confirmation</SectionHeader>
          {pending_confirm.map((c) => (
            <CoverRow key={c.id} c={c} actions={
              <>
                <button className="btn btn-success btn-sm" onClick={() => onConfirm(c.id)} disabled={saving}>
                  <CheckCircle size={12} /> Confirm
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => onCancel(c.id)} disabled={saving}>
                  <XIcon size={12} /> Reject
                </button>
              </>
            } />
          ))}
        </>
      )}

      {my_offers.length > 0 && (
        <>
          <SectionHeader>My offers (waiting for confirmation)</SectionHeader>
          {my_offers.map((c) => (
            <CoverRow key={c.id} c={c} actions={
              <button className="btn btn-secondary btn-sm" onClick={() => onCancel(c.id)} disabled={saving}>
                <XIcon size={12} /> Withdraw
              </button>
            } />
          ))}
        </>
      )}
    </div>
  );
}

// ── StaffingCard ──────────────────────────────────────────────────────────────
const STAFFING_STYLE = {
  green:  { color: 'var(--success)', text: 'Enough staff (≥ 115 %)' },
  yellow: { color: 'var(--warning)', text: 'Works, but tight (111–115 %)' },
  red:    { color: 'var(--danger)',  text: 'Not enough to keep the 10 % buffer — shifts may stay open' },
  none:   { color: 'var(--text-muted)', text: 'No regular shifts this month' },
};
const TRIGGER_LABEL = {
  created: 'after plan creation',
  window_closed: 'after the availability window closed',
  manual: 'rechecked manually',
};

function monthName(month) {
  return format(parseISO(`${month}-01`), 'MMMM yyyy');
}

// Hiring recommendation of a yellow / red month (expandable)
function StaffingRecommendation({ rec }) {
  const [open, setOpen] = useState(false);
  const cov = rec.coverage || {};
  const weakDays = new Set((cov.hardest || []).filter((h) => h.kind === 'weekday').map((h) => h.weekday));
  const estimate = cov.mode === 'estimate';
  const th = { textAlign: 'left', padding: '5px 8px', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' };
  const td = { padding: '5px 8px', fontSize: '0.8rem' };
  return (
    <div style={{ marginTop: '6px' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px', minHeight: '32px',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: 'var(--primary-dark)', fontWeight: 600, fontSize: '0.8rem',
        }}
      >
        Recommendation
        <ChevronDown size={13} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }} />
      </button>
      {open && (
        <div style={{ marginTop: '4px', padding: '10px 12px', borderRadius: 'var(--radius)', background: 'var(--primary-xlight, #EBF4FB)', border: '1px solid var(--primary-light)' }}>
          {rec.text && <p style={{ margin: '0 0 4px', fontWeight: 600 }}>{rec.text}</p>}
          <p style={{ margin: '0 0 6px', color: 'var(--text-muted)' }}>{rec.min_text}</p>
          {rec.alternatives_text?.length > 0 && (
            <p style={{ margin: '0 0 8px' }}>
              <span style={{ color: 'var(--text-muted)' }}>Alternatives: </span>
              {rec.alternatives_text.join(' · ')}
            </p>
          )}
          {cov.hardest_text && <p style={{ margin: '0 0 2px' }}>{cov.hardest_text}.</p>}
          {cov.hint && <p style={{ margin: '0 0 8px', fontWeight: 600, color: 'var(--primary-dark)' }}>{cov.hint}</p>}
          <p style={{ margin: '0 0 6px', fontSize: '0.75rem', color: estimate ? '#b45309' : 'var(--text-muted)', fontWeight: estimate ? 600 : 400 }}>
            {cov.label}
          </p>
          {cov.weekdays?.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--surface, #fff)', borderRadius: 'var(--radius)' }}>
                <thead>
                  <tr>
                    <th style={th}>Weekday</th>
                    <th style={{ ...th, textAlign: 'right' }}>Slots</th>
                    <th style={{ ...th, textAlign: 'right' }}>Coverage</th>
                    {estimate && <th style={{ ...th, textAlign: 'right' }}>Unfilled before</th>}
                  </tr>
                </thead>
                <tbody>
                  {cov.weekdays.map((w) => {
                    const weak = weakDays.has(w.weekday);
                    return (
                      <tr key={w.weekday} style={{ background: weak ? 'var(--warning-light)' : undefined, fontWeight: weak ? 600 : 400 }}>
                        <td style={td}>{w.weekday}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{w.slots}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{w.coverage == null ? '—' : w.coverage.toFixed(2)}</td>
                        {estimate && (
                          <td style={{ ...td, textAlign: 'right' }}>{w.unfilled_rate == null ? '—' : `${Math.round(w.unfilled_rate * 100)} %`}</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p style={{ margin: '6px 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            Coverage = available people-shifts ÷ slots (1.15 or more is comfortable). People count by their share of preferred shifts.
          </p>
        </div>
      )}
    </div>
  );
}

function StaffingCard({ check, loading, rechecking, onRecheck }) {
  const pct = (r) => (r == null ? '—' : `${Math.round(r * 100)} %`);
  return (
    <div
      className="card"
      style={{ marginBottom: '16px', padding: '14px 16px', border: '1px solid var(--primary-light)', background: 'var(--primary-xlight, #EBF4FB)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <div>
          <p style={{ fontWeight: 600, fontSize: '0.9rem', margin: 0 }}>Staffing check</p>
          {check?.checked_at && (
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>
              {format(new Date(check.checked_at), 'dd.MM.yyyy HH:mm')} · {TRIGGER_LABEL[check.trigger] || check.trigger}
              {check.include_submissions ? ' · blocked days & weekends subtracted' : ''}
            </p>
          )}
        </div>
        <button className="btn btn-secondary btn-sm" onClick={onRecheck} disabled={rechecking || loading}>
          <RefreshCw size={13} />
          {rechecking ? 'Checking…' : 'Recheck staffing'}
        </button>
      </div>

      {loading ? (
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>Loading…</p>
      ) : !check?.months ? (
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>Not checked yet.</p>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {check.months.map((m) => {
              const st = STAFFING_STYLE[m.status] || STAFFING_STYLE.none;
              return (
                <div
                  key={m.month}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap',
                    padding: '8px 10px', background: 'var(--surface, #fff)', borderRadius: 'var(--radius)',
                    borderLeft: `4px solid ${st.color}`, fontSize: '0.82rem',
                  }}
                >
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: st.color, marginTop: '4px', flexShrink: 0 }} aria-hidden />
                  <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    <p style={{ margin: 0 }}>
                      <strong>{monthName(m.month)}</strong>
                      {m.days_in_plan < m.days_in_month && (
                        <span style={{ color: 'var(--text-muted)' }}> ({m.days_in_plan}/{m.days_in_month} days)</span>
                      )}
                      {' · '}<strong style={{ color: st.color }}>{pct(m.ratio)}</strong>
                    </p>
                    <p style={{ margin: 0, color: 'var(--text-muted)' }}>
                      {m.supply_shifts} shifts available for {m.demand_slots} slots · {m.supply_hours} h vs. {m.demand_hours} h ({pct(m.hours_ratio)})
                    </p>
                    <p style={{ margin: 0, color: st.color, fontWeight: 500 }}>{st.text}</p>
                    {m.recommendation && <StaffingRecommendation rec={m.recommendation} />}
                  </div>
                </div>
              );
            })}
          </div>
          {check.users_without_values?.length > 0 && (
            <div
              style={{
                marginTop: '8px', padding: '8px 12px', fontSize: '0.8rem',
                background: 'var(--warning-light)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)',
              }}
            >
              <strong>No contract hours and no preferred shifts:</strong>{' '}
              {check.users_without_values.map((u) => u.name).join(', ')} — will not receive regular shifts — set values in the user panel.
            </div>
          )}
        </>
      )}
      {!loading && check && <QualificationNotes check={check} />}
    </div>
  );
}

// Qualification notes of the staffing card (always current, also before a check)
function QualificationNotes({ check }) {
  const warnings = check.qualification_warnings || [];
  const noType = check.users_without_qualified_type || [];
  const conflicts = check.qualification_conflicts || [];
  if (!warnings.length && !noType.length && !conflicts.length) return null;
  const box = {
    marginTop: '8px', padding: '8px 12px', fontSize: '0.8rem',
    background: 'var(--warning-light)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)',
  };
  return (
    <>
      {warnings.length > 0 && (
        <div style={box}>
          {warnings.map((w) => (
            <div key={w.shift_type}>
              ⚠️ <strong>{w.label}</strong>: only {w.qualified} {w.qualified === 1 ? 'person' : 'people'} qualified
            </div>
          ))}
        </div>
      )}
      {noType.length > 0 && (
        <div style={box}>
          <strong>Not qualified for any shift type:</strong> {noType.map((u) => u.name).join(', ')} — can't be scheduled for
          regular shifts and don't count toward the supply.
        </div>
      )}
      {conflicts.length > 0 && (
        <div style={{ ...box, background: 'var(--danger-light)', borderColor: 'var(--danger)' }}>
          <strong>
            {conflicts.length} assigned {conflicts.length === 1 ? 'shift' : 'shifts'} without qualification:
          </strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
            {conflicts.map((c) => (
              <li key={c.slot_id}>
                {c.user_name} · {c.label} on {format(parseISO(c.date), 'EEE, d MMM')} {c.start_time.slice(0, 5)}–{c.end_time.slice(0, 5)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// ── GenerateConfirmModal ──────────────────────────────────────────────────────
function GenerateConfirmModal({ isOpen, onClose, onGenerate, generating, activePeriod, pendingVacation = [], generatorSettings, onSettingsChanged, onUnpinAll }) {
  const groupFrom = generatorSettings?.threshold ?? 4;
  const maxStreak = generatorSettings?.maxStreak ?? 5;
  const maxDaysInRow = generatorSettings?.numbers?.MAX_DAYS_IN_ROW ?? 5;
  const varInRow = generatorSettings?.variety?.maxInRow ?? 2;
  const varPerWeek = generatorSettings?.variety?.maxPerWeek ?? 3;
  const [infoOpen, setInfoOpen] = useState(false);
  const hasPending = pendingVacation.length > 0;

  // Variant: levels for this run only (pre-filled with the defaults) + variant number
  const [variantOpen, setVariantOpen] = useState(false);
  const [runLevels, setRunLevels] = useState(null);
  const [variant, setVariant] = useState(1);
  const defaults = generatorSettings?.levels;
  useEffect(() => {
    if (isOpen && defaults) setRunLevels({ ...defaults });
  }, [isOpen, defaults]);
  useEffect(() => {
    if (isOpen) { setVariant(1); setVariantOpen(false); }
  }, [isOpen]);
  const maxVariant = generatorSettings?.max_variant ?? 9999;
  const variantOk = Number.isInteger(Number(variant)) && Number(variant) >= 1 && Number(variant) <= maxVariant;
  const changedRules = defaults && runLevels ? Object.keys(defaults).filter((k) => runLevels[k] !== defaults[k]) : [];
  const generate = () => onGenerate({ levels: runLevels || undefined, variant: Number(variant) });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Generate Roster" size="sm">
      {hasPending && (
        <div
          style={{
            padding: '12px 14px', marginBottom: '14px',
            background: 'var(--warning-light)', border: '1px solid var(--warning)',
            borderRadius: 'var(--radius)', fontSize: '0.85rem',
          }}
        >
          <p style={{ fontWeight: 600, marginBottom: '6px' }}>
            {pendingVacation.length} pending vacation request{pendingVacation.length !== 1 ? 's' : ''} in this plan — decide {pendingVacation.length !== 1 ? 'them' : 'it'} first or generate anyway?
          </p>
          <ul style={{ margin: '0 0 8px', paddingLeft: '18px' }}>
            {pendingVacation.map((r) => (
              <li key={r.id}><strong>{r.user_name}</strong> — {formatVacationRange(r.start_date, r.end_date)}</li>
            ))}
          </ul>
          <p style={{ margin: 0, color: 'var(--text-light)' }}>
            Only approved vacation keeps people free.{' '}
            <Link to="/vacation" style={{ color: 'var(--primary-dark)', fontWeight: 600 }}>Go to Vacation requests →</Link>
          </p>
        </div>
      )}
      <p style={{ marginBottom: '8px' }}>
        Auto-assign shifts for{' '}
        <strong>
          {activePeriod?.start_date} – {activePeriod?.end_date}
        </strong>{' '}
        based on team availability.
      </p>
      <p style={{ fontSize: '0.85rem', color: 'var(--danger)', marginBottom: activePeriod?.pinned_count ? '8px' : '16px' }}>
        Existing draft assignments for this period will be replaced{activePeriod?.pinned_count ? ' — except pinned shifts' : ''}.
      </p>
      {activePeriod?.pinned_count > 0 && (
        <p style={{ fontSize: '0.85rem', marginBottom: '16px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span>
            📌 <strong>{activePeriod.pinned_count} shift{activePeriod.pinned_count === 1 ? '' : 's'} pinned</strong> — kept and
            counted for everyone.
          </span>
          {onUnpinAll && (
            <button type="button" onClick={onUnpinAll}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary-dark)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }}>
              Unpin all
            </button>
          )}
        </p>
      )}

      {/* Variant settings: levels for this run only + variant number */}
      {generatorSettings?.rules && runLevels && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px', marginBottom: '12px' }}>
          <button
            type="button"
            onClick={() => setVariantOpen((o) => !o)}
            aria-expanded={variantOpen}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer',
              padding: 0, fontSize: '0.85rem', color: 'var(--primary-dark)', fontWeight: 600, minHeight: '32px',
            }}
          >
            <Wand2 size={14} />
            Variant settings
            <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
              {' '}— variant {variant}{changedRules.length ? ` · ${changedRules.length} rule${changedRules.length === 1 ? '' : 's'} changed` : ' · defaults'}
            </span>
            <ChevronDown size={13} style={{ transform: variantOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }} />
          </button>
          {variantOpen && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', margin: '0 0 10px' }}>
                Changes apply to this run only — the defaults stay as they are. &quot;Changed&quot; marks a level that
                differs from the default. The same data, settings and variant number always give the same draft; a new
                variant number decides ties differently.
              </p>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <div>
                  <label htmlFor="gen-variant" className="form-label" style={{ fontSize: '0.8rem' }}>Variant number</label>
                  <input
                    id="gen-variant"
                    type="number"
                    min="1"
                    max={maxVariant}
                    step="1"
                    className="form-input"
                    style={{ width: '110px', minHeight: '40px' }}
                    value={variant}
                    onChange={(e) => setVariant(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ minHeight: '40px' }}
                  onClick={() => setVariant((v) => Math.min(maxVariant, (Number(v) || 0) + 1))}
                >
                  New variant
                </button>
              </div>
              <GeneratorTopics
                meta={generatorSettings}
                levels={runLevels}
                onLevels={setRunLevels}
                numbers={generatorSettings.numbers}
                levelBaseline={defaults}
                numberBaseline={generatorSettings.factory_numbers}
                baselineName="Default"
                numbersHint="Set in Admin → Roster Generator."
                idPrefix="run"
              />
              <DefaultLevelActions
                meta={generatorSettings}
                levels={runLevels}
                onSaved={(saved) => { setRunLevels({ ...saved }); onSettingsChanged?.(); }}
              />
            </div>
          )}
        </div>
      )}

      {/* Collapsible info panel */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
        <button
          type="button"
          onClick={() => setInfoOpen((o) => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            fontSize: '0.82rem',
            color: 'var(--primary)',
            fontWeight: 500,
          }}
        >
          <Info size={14} />
          How does this work?
          <ChevronDown
            size={13}
            style={{
              transform: infoOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.18s',
              marginLeft: '2px',
            }}
          />
        </button>

        {infoOpen && (
          <div
            style={{
              marginTop: '12px',
              padding: '12px 14px',
              background: 'var(--primary-xlight, #EBF4FB)',
              borderRadius: 'var(--radius)',
              fontSize: '0.8rem',
              color: 'var(--text)',
              lineHeight: 1.55,
            }}
          >
            <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Monthly targets (per calendar month)</p>
            <ul style={{ margin: '0 0 10px', paddingLeft: '18px' }}>
              <li><strong>Qualifications:</strong> nobody is scheduled automatically for a shift type they are not qualified for (set per person in the user panel). Admins can still assign them manually after a warning.</li>
              <li><strong>What counts:</strong> only regular shifts. Special events, the OM-Meeting and Amt hours never count toward targets, minimums or weekly limits.</li>
              <li><strong>Minimum:</strong> contracted hours/week × 4.33 — the contract always wins. People below their minimum are picked first.</li>
              <li><strong>Target:</strong> 90 % of preferred shifts/week × 4.33. In the normal passes nobody gets more than their target — unless they are still below their minimum hours (e.g. because they got short shifts).</li>
              <li><strong>Emergency max:</strong> 110 % of preferred shifts. Only used at the very end for shifts that would otherwise stay open. Every emergency assignment is listed in the report.</li>
              <li><strong>Pro-rating:</strong> months only partly inside the plan, approved vacation and absences reduce all values proportionally.</li>
              <li><strong>Minijob:</strong> the monthly hours cap is a legal earnings limit and is never exceeded — here events do count. The mandatory OM-Meeting is assigned anyway, with a warning.</li>
              <li>No preferred shifts set → derived from contract hours. Neither set → no regular shifts at all.</li>
            </ul>

            <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Eligibility (hard rules)</p>
            <ul style={{ margin: '0 0 10px', paddingLeft: '18px' }}>
              <li>No double shift on the same day — hard block, no exceptions.</li>
              <li>User has a full-day block on that date → skipped.</li>
              <li>User has a morning half-day block → skipped for shifts starting before 13:00.</li>
              <li>User has an afternoon half-day block → skipped for shifts ending after 13:00.</li>
              <li>User has this weekend blocked (specific weekends, or off in their weekend rhythm) → skipped on Sat/Sun.</li>
              <li>
                &quot;Every other weekend off&quot;: the system assigns Rhythm A or B before filling shifts, balancing
                the people available per weekend, and keeps it for later plans — also when no availability is submitted.
              </li>
              <li>User has an admin-assigned absence covering that date → skipped.</li>
              <li>User is on approved vacation that day (any start day, whole weeks) → skipped. Pending or rejected requests don't block.</li>
              <li>User has PMS on that date → skipped.</li>
              <li>User has reached their monthly target (normal passes) or emergency max (emergency pass) → skipped.</li>
              <li>
                <strong>Max. {maxDaysInRow} days in a row</strong> for everyone (absolute limit, set in Admin → Settings → Roster Generator).
                Every shift counts — regular shifts, events and the OM-Meeting — across weeks, months and plans. A shift left open because
                of this shows &quot;would exceed {maxDaysInRow} days in a row&quot;. The OM-Meeting is still assigned, with a red warning;
                admins can go over the limit by hand after a warning.
              </li>
              <li><strong>Max. 10 h per day and min. 11 h rest</strong> between shifts (events and the OM-Meeting count, also shifts of the neighbouring plans) → skipped. A shift left open because of this shows the reason &quot;would break 10 h day / 11 h rest&quot;. Admins can still assign manually after a warning.</li>
            </ul>

            <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Who gets a shift (soft rules)</p>
            <ul style={{ margin: '0 0 10px', paddingLeft: '18px' }}>
              <li><strong>Below contract minimum</strong> — people who haven't reached their contracted hours this month come first. Not adjustable.</li>
              <li><strong>Even out hours</strong> — people furthest below their own monthly target come next, so people with few and many shifts are treated alike.</li>
              <li><strong>Favourite shift types</strong> — people get the shift types they ranked highest, matched by the template's shift type. Templates without a type count as neutral; people without a submission get no preference.</li>
              <li><strong>Spread across the week</strong> — nobody gets clearly more shifts in one calendar week than they asked for (2.5/week → 2 or 3 is fine, 4 is avoided).</li>
              <li>
                <strong>Group into blocks</strong> — people who want {groupFrom} or more shifts per week get their shifts on
                consecutive days (ideal block length {maxStreak} days, never above the absolute max), so they have several days off afterwards; single days off
                between two shifts are avoided. On Mon–Fri the same time band (early / middle / late) as the day before is
                preferred. Special events neither build nor break a block. Afterwards, two people may trade shifts of the same
                template in the same week if that removes single days off without making anything else worse.
              </li>
              <li><strong>Vary shift types</strong> — the same shift type at most {varInRow} days in a row and {varPerWeek} times per week (Mon–Sun) unless nobody else can take it; regular shifts only, also across plans.</li>
              <li><strong>Pairing wishes</strong> — &quot;prefer&quot; colleagues are put on the same day, &quot;avoid&quot; pairs are kept apart (regular shifts only).</li>
              <li><strong>Fair turns at events</strong> — people who did an event less often in the 6 months before this plan get the next turn.</li>
            </ul>

            <p style={{ margin: '0 0 8px', color: 'var(--text-muted)' }}>
              <strong>OM-Meeting (👥):</strong> Mandatory — every active OM role holder is assigned to their own slot before anything else. Vacation, absences and sickness leave the slot open; availability blocks are overridden with a warning. No other shift may overlap the meeting.
              <br />
              <strong>Special events (🧹):</strong> Event slots are filled next — priority role holder is assigned to slot 1, then the remaining slots are filled by all eligible users. Users who worked the event last month are excluded (rotation). Regular shifts are filled afterwards in the usual two-pass order.
            </p>
            <p style={{ margin: '0 0 8px', color: 'var(--text-muted)' }}>
              <strong>Levels and ties:</strong> each soft rule counts as much as its level says — Normal is the standard,
              Low half as much, High one and a half times, Very high twice, Off not at all. When two people are equally
              suitable, the one further below their monthly target wins, then the one qualified for fewer shift types (so
              flexible people stay free); otherwise the variant number decides.
              Within each day the shift with the fewest possible people is filled first, and nobody is used up on a shift
              others could take when they are the only one for another open shift that day.
            </p>
            <p style={{ margin: '0 0 8px', color: 'var(--text-muted)' }}>
              <strong>Passes:</strong> Pass 1 fills shifts using only users who submitted their availability. Pass 2 fills remaining gaps with users who did not submit (no preference bonus). The emergency pass then fills what is still open, up to 110 % — submitters first.
            </p>
            <p style={{ margin: '0 0 8px', color: 'var(--text-muted)' }}>
              <strong>Staffing check (traffic light):</strong> per month, the sum of everyone's preferred shifts (100 %) is compared with the number of regular shifts. 🟢 ≥ 115 % · 🟡 111–115 % works, but tight · 🔴 below 111 % — not enough to keep the 10 % buffer, shifts may stay open. It runs when a plan is created and again when the availability window closes (then blocked days and weekends are subtracted).
            </p>
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>
              Contracted hours, preferred shifts and minijob caps are configured per user in the Team page.
            </p>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
        <button className="btn btn-secondary" onClick={onClose} disabled={generating}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={generate} disabled={generating || !variantOk}>
          {generating ? 'Generating…' : hasPending ? 'Generate anyway' : 'Generate'}
        </button>
      </div>
    </Modal>
  );
}

// ── MobileWeekTable ───────────────────────────────────────────────────────────
const WEEK_COLS = [
  { key: 'eb',   abbr: 'EB',   fullName: 'Early – Backstage', match: (t) => t === 'Early - Backstage' },
  { key: 'ef',   abbr: 'EF',   fullName: 'Early – FoH',       match: (t) => t === 'Early - FoH' },
  { key: 'tl',   abbr: 'TL',   fullName: 'Tagesleitung',      match: (t) => t?.startsWith('Tagesleitung') },
  { key: 'sup',  abbr: 'SUP',  fullName: 'Support',           match: (t) => t?.startsWith('Support') },
  { key: 'late', abbr: 'LATE', fullName: 'Lateshift',         match: (t) => t === 'Lateshift' },
];
const DE_DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function MobileWeekTable({
  events, weekStart, onPrevWeek, onNextWeek, onToday,
  currentUser, isAdmin, holidaySet, holidayNames, sundayBonuses, peopleByDate,
  onSlotClick, loading,
}) {
  const { colors } = useUserColors();
  const scrollRef = useRef(null);
  const todayColRef = useRef(null);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const endDate = new Date(weekStart);
  endDate.setDate(endDate.getDate() + 6);

  // Scroll so today's column is leftmost; reset to 0 when week changes. The
  // container only exists while not loading, and may be gone by the next frame
  // (view unmounted / switched), so the ref is checked inside the frame.
  useEffect(() => {
    if (loading) return undefined;
    const frame = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollLeft = todayColRef.current ? todayColRef.current.offsetLeft : 0;
    });
    return () => cancelAnimationFrame(frame);
  }, [weekStart, loading]);

  // Slots grouped by date, sorted by start_time
  const slotsByDate = {};
  for (const ev of events) {
    const slot = ev.extendedProps?.slot;
    if (!slot) continue;
    if (!slotsByDate[slot.date]) slotsByDate[slot.date] = [];
    slotsByDate[slot.date].push(slot);
  }
  for (const dk of Object.keys(slotsByDate)) {
    slotsByDate[dk].sort(compareSlots);
  }

  // Per-day assigned count for double-shift warning (admin only)
  const userDayCount = {};
  if (isAdmin) {
    for (const ev of events) {
      const slot = ev.extendedProps?.slot;
      if (!slot || !slot.assigned_user_id) continue;
      const key = `${slot.date}-${slot.assigned_user_id}`;
      userDayCount[key] = (userDayCount[key] || 0) + 1;
    }
  }

  const getShortLabel = (templateName) => {
    if (!templateName) return '—';
    const col = WEEK_COLS.find((c) => c.match(templateName));
    return col ? col.abbr : templateName.slice(0, 3);
  };

  const navBtn = { width: '44px', height: '44px', flexShrink: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--white)', cursor: 'pointer', fontSize: '1.3rem', display: 'flex', alignItems: 'center', justifyContent: 'center' };

  return (
    <div>
      {/* Week navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '6px' }}>
        <button onClick={onPrevWeek} aria-label="Previous week" style={navBtn}>‹</button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>{format(weekStart, 'd MMM')} – {format(endDate, 'd MMM yyyy')}</div>
          <button onClick={onToday} style={{ marginTop: '2px', fontSize: '0.75rem', color: 'var(--primary-dark)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', textDecoration: 'underline' }}>Today</button>
        </div>
        <button onClick={onNextWeek} aria-label="Next week" style={navBtn}>›</button>
      </div>

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}><div className="spinner" /></div>}

      {!loading && (
        <div
          ref={scrollRef}
          style={{
            display: 'flex',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            gap: '8px',
            paddingBottom: '8px',
            alignItems: 'flex-start',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {days.map((day, i) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const isToday = dateStr === todayStr;
            const isHoliday = holidaySet.has(dateStr);
            const holidayName = holidayNames[dateStr];
            const bonus = sundayBonuses[dateStr];
            const isSun = day.getDay() === 0;
            const people = peopleByDate[dateStr];
            const daySlots = slotsByDate[dateStr] || [];

            return (
              <div
                key={dateStr}
                ref={isToday ? todayColRef : undefined}
                style={{
                  flex: '0 0 calc((100vw - 48px) / 3.3)',
                  scrollSnapAlign: 'start',
                  minWidth: '0',
                  border: `1px solid ${isToday ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius)',
                  background: 'var(--white)',
                  overflow: 'hidden',
                }}
              >
                {/* Column header */}
                <div style={{
                  position: 'sticky', top: 0, zIndex: 1,
                  padding: '8px 6px 6px',
                  background: isToday ? 'var(--primary-xlight)' : isHoliday ? 'rgba(255,152,0,0.1)' : 'var(--bg)',
                  borderBottom: `2px solid ${isToday ? 'var(--primary)' : 'var(--border)'}`,
                  textAlign: 'center',
                }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: isToday ? 'var(--primary-dark)' : 'var(--text)' }}>
                    {DE_DAYS[i]}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', fontWeight: 500 }}>
                    {day.getDate()}.{String(day.getMonth() + 1).padStart(2, '0')}.
                  </div>
                  {isHoliday && holidayName && (
                    <div style={{ fontSize: '0.58rem', color: '#E65100', lineHeight: 1.2, marginTop: '2px' }}>✦ {holidayName}</div>
                  )}
                  {(isSun || bonus) && (
                    <div style={{ fontSize: '0.58rem', color: 'var(--primary-dark)', marginTop: '1px' }}>+bonus</div>
                  )}
                  {people && (
                    <div style={{ marginTop: '3px', display: 'flex', justifyContent: 'center' }}>
                      <DayPeople entry={people} size={16} align="center" dateLabel={format(day, 'EEE, d MMM')} />
                    </div>
                  )}
                </div>

                {/* Shift cards */}
                <div style={{ padding: '6px 4px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  {daySlots.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</div>
                  ) : (
                    daySlots.map((slot) => {
                      const isMyShift = slot.assigned_user_id === currentUser?.id;
                      const isOpen = !slot.assigned_user_id;
                      const hasDoubleShift = isAdmin && slot.assigned_user_id &&
                        userDayCount[`${slot.date}-${slot.assigned_user_id}`] > 1;
                      const isDraft = slot.period_status === 'draft';
                      const shortLabel = slotTitle(slot, getShortLabel(slot.template_name));
                      const timeLabel = `${fmt(slot.start_time)}–${fmt(slot.end_time)}`;
                      const look = shiftLook(slot, colors?.[slot.assigned_user_id]);

                      return (
                        <button
                          key={slot.id}
                          onClick={() => onSlotClick(slot)}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left',
                            padding: '7px 6px',
                            ...lookStyle(look),
                            borderRadius: '6px',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <span style={{
                              fontWeight: isMyShift ? 800 : 700, fontSize: '0.9rem',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                            }}>
                              {isOpen ? <OpenLabel size={13} /> : (slot.assigned_user_name?.split(' ')[0] || '?')}
                            </span>
                            {slot.is_training ? <TrainingChip size={11} /> : null}
                            {!isOpen && slot.is_pinned ? <span style={{ ...CHIP_STYLE, fontSize: '0.7rem', padding: '0 2px' }} title="Pinned — kept when generating again" aria-label="Pinned">📌</span> : null}
                            {slot._diff && <span style={{ ...CHIP_STYLE, fontSize: '0.7rem', color: '#C2410C', fontWeight: 700 }} title="Differs between the compared versions">≠</span>}
                            {hasDoubleShift && <span style={{ ...CHIP_STYLE, fontSize: '0.7rem', padding: '0 2px' }} title="Double shift">⚠️</span>}
                          </div>
                          <div style={{ fontSize: '0.68rem', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {shortLabel} · {timeLabel}
                          </div>
                          {isDraft && (
                            <div style={{ fontSize: '0.6rem', fontStyle: 'italic' }}>Draft</div>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── MobileRosterView ──────────────────────────────────────────────────────────
function MobileRosterView({
  events, weekStart, onPrevWeek, onNextWeek,
  filter, onFilterChange, hideFilter = false,
  currentUser, isAdmin,
  holidaySet, holidayNames, sundayBonuses, peopleByDate,
  onSlotClick, loading,
}) {
  const { colors } = useUserColors();
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const slotsByDate = {};
  for (const ev of events) {
    const slot = ev.extendedProps?.slot;
    if (!slot) continue;
    if (!slotsByDate[slot.date]) slotsByDate[slot.date] = [];
    slotsByDate[slot.date].push(slot);
  }
  for (const dk of Object.keys(slotsByDate)) {
    slotsByDate[dk].sort(compareSlots);
  }

  const endDate = new Date(weekStart);
  endDate.setDate(endDate.getDate() + 6);

  const toggleBtn = (active) => ({
    flex: 1, padding: '11px', border: 'none', cursor: 'pointer',
    background: active ? 'var(--primary)' : 'var(--white)',
    color: active ? 'var(--text)' : 'var(--text-light)',
    fontWeight: active ? 600 : 400, fontSize: '0.92rem',
  });

  let hasAnyShift = false;

  return (
    <div>
      {/* All / My toggle (hidden while the admin shows one person's plan) */}
      {!hideFilter && (
        <div style={{ display: 'flex', border: '1.5px solid var(--primary)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: '14px' }}>
          <button onClick={() => onFilterChange('all')} style={toggleBtn(filter === 'all')}>All shifts</button>
          <button onClick={() => onFilterChange('my')} style={{ ...toggleBtn(filter === 'my'), borderLeft: '1.5px solid var(--primary)' }}>My shifts</button>
        </div>
      )}

      {/* Week navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '8px' }}>
        <button onClick={onPrevWeek} aria-label="Previous week" style={{ width: '44px', height: '44px', flexShrink: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--white)', cursor: 'pointer', fontSize: '1.3rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
        <div style={{ textAlign: 'center', flex: 1, fontWeight: 600, fontSize: '0.92rem' }}>
          {format(weekStart, 'd MMM')} – {format(endDate, 'd MMM yyyy')}
        </div>
        <button onClick={onNextWeek} aria-label="Next week" style={{ width: '44px', height: '44px', flexShrink: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--white)', cursor: 'pointer', fontSize: '1.3rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
      </div>

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}><div className="spinner" /></div>}

      {!loading && days.map((day) => {
        const dateStr = format(day, 'yyyy-MM-dd');
        let daySlots = slotsByDate[dateStr] || [];
        if (filter === 'my') {
          daySlots = daySlots.filter((s) => !s.assigned_user_id || s.assigned_user_id === currentUser?.id);
        }
        if (filter === 'my' && daySlots.length === 0) return null;
        if (daySlots.length > 0) hasAnyShift = true;

        const isHoliday = holidaySet.has(dateStr);
        const holidayName = holidayNames[dateStr];
        const bonus = sundayBonuses[dateStr];
        const people = peopleByDate[dateStr];
        const isSunday = day.getDay() === 0;

        return (
          <div key={dateStr} style={{ marginBottom: '14px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
              padding: '9px 12px', borderRadius: 'var(--radius)', marginBottom: '6px',
              background: isHoliday ? 'rgba(255,152,0,0.12)' : isSunday ? 'var(--bg)' : 'var(--primary-xlight)',
              borderLeft: `3px solid ${isHoliday ? 'var(--warning)' : isSunday ? 'var(--primary-dark)' : 'var(--primary)'}`,
            }}>
              <span style={{ fontWeight: 700, fontSize: '1rem' }}>{format(day, 'EEE, d MMM')}</span>
              {holidayName && <span style={{ fontSize: '0.75rem', color: 'var(--warning)', fontWeight: 600 }}>✦ {holidayName}</span>}
              {bonus && <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#E65100', background: '#FFF3E0', borderRadius: '3px', padding: '1px 5px' }}>{bonus.bonus_type === 'high_50' ? '+50%' : '+25%'}</span>}
              {people && (
                <span style={{ marginLeft: 'auto' }}>
                  <DayPeople entry={people} size={20} align="flex-end" dateLabel={format(day, 'EEE, d MMM')} />
                </span>
              )}
            </div>

            {daySlots.length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', padding: '6px 12px' }}>No shifts</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {daySlots.map((slot) => {
                  const isMyShift = slot.assigned_user_id === currentUser?.id;
                  const isOpen = !slot.assigned_user_id;
                  const isDraft = slot.period_status === 'draft';
                  const look = shiftLook(slot, colors?.[slot.assigned_user_id]);
                  return (
                    <button key={slot.id} onClick={() => onSlotClick(slot)} style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', minHeight: '52px',
                      ...lookStyle(look),
                      borderRadius: 'var(--radius)', cursor: 'pointer', textAlign: 'left', width: '100%',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                          {slot.is_training ? <TrainingChip size={13} style={{ marginRight: '5px' }} /> : null}
                          {slotTitle(slot)}
                          {isDraft && <span style={{ marginLeft: '6px', fontSize: '0.7rem', fontWeight: 400, fontStyle: 'italic' }}>draft</span>}
                        </div>
                        <div style={{ fontSize: '0.85rem', marginTop: '2px' }}>{fmt(slot.start_time)} – {fmt(slot.end_time)}</div>
                      </div>
                      <div style={{ flexShrink: 0, textAlign: 'right' }}>
                        <div style={{ fontSize: '0.92rem', fontWeight: isOpen ? 800 : isMyShift ? 700 : 500 }}>
                          {slot._diff && <span style={{ ...CHIP_STYLE, color: '#C2410C', fontWeight: 700, marginRight: '4px' }} title="Differs between the compared versions">≠</span>}
                          {!isOpen && slot.is_pinned ? <span style={{ ...CHIP_STYLE, padding: '0 2px', marginRight: '4px' }} title="Pinned — kept when generating again" aria-label="Pinned">📌</span> : null}
                          {isOpen ? <OpenLabel size={14} /> : (slot.assigned_user_name || '—')}
                        </div>
                        {slot.pending_cover_type && (
                          <div style={{ fontSize: '0.72rem', fontWeight: 600, marginTop: '3px' }}>
                            <span style={{ ...CHIP_STYLE, color: slot.pending_cover_type === 'sick_cover' ? '#C62828' : '#C2410C' }}>
                              {slot.pending_cover_type === 'sick_cover' ? '🏥 Cover needed' : '↔ Swap wanted'}
                            </span>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {!loading && filter === 'my' && !hasAnyShift && (
        <div className="empty-state">
          <p>No shifts assigned to you this week.</p>
          <p style={{ marginTop: '6px', fontSize: '0.85rem' }}>Switch to "All shifts" to see the full roster.</p>
        </div>
      )}
    </div>
  );
}

// ── RosterPage ────────────────────────────────────────────────────────────────
function RosterPage() {
  const { user: currentUser, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const calendarRef = useRef(null);

  const [events, setEvents] = useState([]);
  const [holidaySet, setHolidaySet] = useState(new Set());
  const [holidayNames, setHolidayNames] = useState({});
  const [periods, setPeriods] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [slotModal, setSlotModal] = useState(null);
  const [periodModal, setPeriodModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateConfirm, setGenerateConfirm] = useState(false);
  const [pendingVacation, setPendingVacation] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [generationReport, setGenerationReport] = useState(null);
  const [generatorSettings, setGeneratorSettings] = useState(null);
  // Draft versions dialog + "show differing shifts" highlight (slot ids)
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [diffHighlight, setDiffHighlight] = useState(null);
  // One-time notice: shifts pinned automatically by the update
  const [pinNoticeInfo, setPinNoticeInfo] = useState(null);
  useEffect(() => { setDiffHighlight(null); }, [activePeriod?.id]);

  // Cover request state
  const [shiftActionModal, setShiftActionModal] = useState(null);
  const [coverConfirm, setCoverConfirm] = useState(null);
  const [swapPickerModal, setSwapPickerModal] = useState(null);
  const [openCovers, setOpenCovers] = useState([]);
  const [myCovers, setMyCovers] = useState({ my_open: [], pending_confirm: [], my_offers: [] });
  const [myShiftsForSwap, setMyShiftsForSwap] = useState([]);
  const [coverSaving, setCoverSaving] = useState(false);

  // Vacation state: approved requests (deduped); absences of the loaded range.
  // peopleByDate (chips per day) is derived below.
  const [approvedVacations, setApprovedVacations] = useState([]);
  const [rangeAbsences, setRangeAbsences] = useState([]);
  const { colors } = useUserColors();
  const renderEventContent = useMemo(() => makeEventContent(colors), [colors]);

  // Sunday bonus state
  const [sundayBonuses, setSundayBonuses] = useState({});
  const [sundayBonusModal, setSundayBonusModal] = useState(null);
  const [bonusSaving, setBonusSaving] = useState(false);

  // Clock in/out state
  const [shiftTimeEntry, setShiftTimeEntry] = useState(undefined);
  const [shiftSicknessRecord, setShiftSicknessRecord] = useState(undefined);
  const [clockModal, setClockModal] = useState(null);
  const [clockSaving, setClockSaving] = useState(false);

  // Force-delete roster state (superadmin only)
  const [deleteRosterModal, setDeleteRosterModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingRoster, setDeletingRoster] = useState(false);

  // Track current calendar range for refresh
  const currentRangeRef = useRef(null);

  // Mobile list view state
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  // Phone tab (Week / List / Month), remembered per user on this device
  const [mobileView, setMobileView] = useState(() => readMobileView(currentUser?.id));
  useEffect(() => { setMobileView(readMobileView(currentUser?.id)); }, [currentUser?.id]);
  const changeMobileView = (v) => {
    setMobileView(v);
    saveMobileView(currentUser?.id, v);
  };
  const [mobileFilter, setMobileFilter] = useState('my');
  const [mobileWeekStart, setMobileWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    d.setHours(0, 0, 0, 0);
    return d;
  });

  useEffect(() => {
    setMobileFilter(isAdmin ? 'all' : 'my');
  }, [isAdmin]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── Cover requests ────────────────────────────────────────────────────────────
  const loadCovers = useCallback(async () => {
    try {
      const [openRes, myRes] = await Promise.all([
        api.get('/shift-covers/open'),
        api.get('/shift-covers/my-covers'),
      ]);
      setOpenCovers(openRes.data || []);
      setMyCovers(myRes.data || { my_open: [], pending_confirm: [], my_offers: [] });
    } catch {
      // silently ignore — covers are supplementary
    }
  }, []);

  // ── Initial data load ───────────────────────────────────────────────────────
  useEffect(() => {
    const currYear = new Date().getFullYear();
    const fetches = [
      api.get('/shifts/berlin-holidays'),                                           // [0]
      api.get('/shifts/roster-periods'),                                            // [1]
      api.get(`/vacation-requests/approved?year=${currYear}`),                     // [2]
      api.get(`/vacation-requests/approved?year=${currYear + 1}`),                 // [3]
    ];
    if (isAdmin) {
      fetches.push(api.get('/users'));                                              // [4]
      fetches.push(api.get('/shifts/templates?is_active=1'));                      // [5]
    }

    Promise.all(fetches.map((p) => p.catch(() => null))).then(
      ([holidaysRes, periodsRes, vacCurrRes, vacNextRes, usersRes, templatesRes]) => {
        if (holidaysRes) {
          const holidays = holidaysRes.data || [];
          const hSet = new Set(holidays.map((h) => h.date));
          const hNames = {};
          holidays.forEach((h) => { hNames[h.date] = h.name; });
          setHolidaySet(hSet);
          setHolidayNames(hNames);
        }
        if (periodsRes) {
          const ps = periodsRes.data || [];
          setPeriods(ps);
          // Auto-select: most recent draft for admins, most recent published for users
          if (isAdmin) {
            setActivePeriod(ps.find((p) => p.status === 'draft') ?? ps[0] ?? null);
          } else {
            setActivePeriod(ps[0] ?? null);
          }
        }
        const allVac = [...(vacCurrRes?.data || []), ...(vacNextRes?.data || [])];
        const seenVac = new Set(); // a request crossing New Year comes back for both years
        setApprovedVacations(allVac.filter((v) => !seenVac.has(v.id) && seenVac.add(v.id)));
        if (usersRes) setUsers((usersRes.data || []).filter((u) => u.is_active));
	if (templatesRes) setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      }
    );
    loadCovers();
  }, [isAdmin, loadCovers]);

  // ── Calendar data fetch ──────────────────────────────────────────────────────
  const fetchCalendarEvents = useCallback(
    async (start, end) => {
      setLoading(true);
      try {
        const res = await api.get(`/shifts/calendar?start=${start}&end=${end}`);
        const data = res.data || [];
        const shiftEvents = data
          .filter((s) => s.type !== 'absence')
          .map((s) => slotToEvent(s));
        // Absences are shown as per-day chips (never as bars over the shifts)
        setRangeAbsences(data.filter((s) => s.type === 'absence'));
        setEvents(shiftEvents);
      } catch {
        toast.error('Failed to load calendar shifts.');
      } finally {
        setLoading(false);
      }
    },
    [currentUser?.id]
  );

  const loadSundayBonuses = useCallback(async (start, end) => {
    const months = new Set();
    const d = new Date(start + 'T12:00:00Z');
    const endDate = new Date(end + 'T12:00:00Z');
    while (d <= endDate) {
      months.add(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
      d.setUTCMonth(d.getUTCMonth() + 1);
    }
    const results = await Promise.all([...months].map((m) => api.get(`/sunday-bonus?month=${m}`).catch(() => null)));
    const merged = {};
    for (const res of results) {
      if (res) for (const row of res.data) merged[row.date] = row;
    }
    setSundayBonuses(merged);
  }, []);

  // ── Mobile: load events when week changes ───────────────────────────────────
  useEffect(() => {
    if (!isMobile || mobileView === 'calendar') return;
    const startStr = format(mobileWeekStart, 'yyyy-MM-dd');
    const end = new Date(mobileWeekStart);
    end.setDate(end.getDate() + 6);
    const endStr = format(end, 'yyyy-MM-dd');
    currentRangeRef.current = { start: startStr, end: endStr };
    fetchCalendarEvents(startStr, endStr);
    loadSundayBonuses(startStr, endStr);
  }, [isMobile, mobileView, mobileWeekStart, fetchCalendarEvents, loadSundayBonuses]);

  const goMobilePrevWeek = useCallback(() => {
    setMobileWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; });
  }, []);

  const goMobileNextWeek = useCallback(() => {
    setMobileWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; });
  }, []);

  const goMobileToday = useCallback(() => {
    setMobileWeekStart(() => {
      const d = new Date();
      const day = d.getDay();
      d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      d.setHours(0, 0, 0, 0);
      return d;
    });
  }, []);

  // ── Person selector (admins): ?user=<id>, kept across weeks, reset per plan ──
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedUserId = isAdmin ? (Number(searchParams.get('user')) || null) : null;
  const setSelectedUserId = useCallback((id) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('user', String(id)); else next.delete('user');
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const prevPeriodIdRef = useRef(null);
  useEffect(() => {
    const id = activePeriod?.id ?? null;
    // The first plan selection keeps a bookmarked ?user=; later plan changes reset it
    if (prevPeriodIdRef.current != null && id !== prevPeriodIdRef.current) setSelectedUserId(null);
    if (id != null) prevPeriodIdRef.current = id;
  }, [activePeriod?.id, setSelectedUserId]);

  // Per-person monthly summary — reloaded whenever the events change (every edit)
  const [userSummary, setUserSummary] = useState(null);
  const [userSummaryLoading, setUserSummaryLoading] = useState(false);
  const [summaryReloadKey, setSummaryReloadKey] = useState(0);
  const reloadSummary = useCallback(() => setSummaryReloadKey((k) => k + 1), []);
  useEffect(() => {
    if (!isAdmin || !activePeriod?.id) { setUserSummary(null); return undefined; }
    let cancelled = false;
    setUserSummaryLoading(true);
    api.get(`/shifts/roster-periods/${activePeriod.id}/user-summary`)
      .then((res) => { if (!cancelled) setUserSummary(res.data || null); })
      .catch(() => { if (!cancelled) setUserSummary(null); })
      .finally(() => { if (!cancelled) setUserSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [isAdmin, activePeriod?.id, events, summaryReloadKey]);
  const summaryForPlan = userSummary?.period?.id === activePeriod?.id ? userSummary : null;

  // Dropdown: active shift workers + anyone assigned in this plan (from the summary)
  const personOptions = useMemo(() => {
    if (summaryForPlan) return summaryForPlan.users;
    return users
      .filter((u) => u.works_shifts)
      .map((u) => ({ id: u.id, name: u.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [summaryForPlan, users]);
  // Drop an unknown ?user= once the plan's people are known
  useEffect(() => {
    if (selectedUserId && summaryForPlan && !summaryForPlan.users.some((u) => u.id === selectedUserId)) {
      setSelectedUserId(null);
    }
  }, [selectedUserId, summaryForPlan, setSelectedUserId]);

  // Vacation / absence chips per date — only the selected person's while one is selected
  const peopleByDate = useMemo(
    () => buildPeopleByDate(approvedVacations, rangeAbsences, selectedUserId),
    [approvedVacations, rangeAbsences, selectedUserId]
  );

  // ── Open slots of the selected plan (admins: counter, filter, next open) ────
  const [openOnly, setOpenOnly] = useState(false);
  useEffect(() => { if (selectedUserId) setOpenOnly(false); }, [selectedUserId]);
  const [planOpenSlots, setPlanOpenSlots] = useState(null); // null = not loaded
  useEffect(() => {
    if (!isAdmin || !activePeriod) { setPlanOpenSlots(null); return undefined; }
    let cancelled = false;
    // Reloaded whenever the visible events change (after every edit / generation)
    api.get(`/shifts/calendar?start=${activePeriod.start_date}&end=${activePeriod.end_date}`)
      .then((res) => {
        if (cancelled) return;
        setPlanOpenSlots((res.data || []).filter((s) =>
          s.type !== 'absence' && s.roster_period_id === activePeriod.id && !s.assigned_user_id));
      })
      .catch(() => { if (!cancelled) setPlanOpenSlots(null); });
    return () => { cancelled = true; };
  }, [isAdmin, activePeriod, events]);
  useEffect(() => { setOpenOnly(false); }, [activePeriod?.id]);
  useEffect(() => { if (planOpenSlots && planOpenSlots.length === 0) setOpenOnly(false); }, [planOpenSlots]);

  const openCounts = useMemo(() => {
    if (!planOpenSlots) return null;
    const eventSlots = planOpenSlots.filter((s) => s.special_event_id).length;
    return { total: planOpenSlots.length, events: eventSlots, regular: planOpenSlots.length - eventSlots };
  }, [planOpenSlots]);

  // Person filter (their slots; their chips via peopleByDate) or "only open shifts" — every view
  const visibleEvents = useMemo(() => {
    let list = events;
    if (selectedUserId) list = events.filter((e) => e.extendedProps?.slot?.assigned_user_id === selectedUserId);
    else if (openOnly) list = events.filter((e) => isOpenSlot(e.extendedProps?.slot));
    // Draft versions → "Show the differing shifts in the roster"
    if (!diffHighlight) return list;
    return list.map((e) => (diffHighlight.has(e.extendedProps?.slot?.id)
      ? {
        ...e, classNames: [...(e.classNames || []), 'fc-event-diff'], borderColor: '#E65100',
        extendedProps: { ...e.extendedProps, slot: { ...e.extendedProps.slot, _diff: true } },
      }
      : e));
  }, [events, openOnly, selectedUserId, diffHighlight]);

  // Open slots per date in the loaded range (month-view day markers; hidden for one person)
  const openByDate = useMemo(() => {
    const out = {};
    if (selectedUserId) return out;
    for (const e of events) {
      const slot = e.extendedProps?.slot;
      if (isOpenSlot(slot)) out[slot.date] = (out[slot.date] || 0) + 1;
    }
    return out;
  }, [events, selectedUserId]);


  const handleDatesSet = useCallback(
    ({ startStr, endStr }) => {
      const start = startStr.slice(0, 10);
      const end = endStr.slice(0, 10);
      currentRangeRef.current = { start, end };
      fetchCalendarEvents(start, end);
      loadSundayBonuses(start, end);
    },
    [fetchCalendarEvents, loadSundayBonuses]
  );

  const refreshCalendar = useCallback(() => {
    if (currentRangeRef.current) {
      const { start, end } = currentRangeRef.current;
      fetchCalendarEvents(start, end);
    }
  }, [fetchCalendarEvents]);

  // ── Holiday styling ──────────────────────────────────────────────────────────
  const dayCellClassNames = useCallback(
    ({ date }) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return holidaySet.has(dateStr) ? ['fc-day-holiday'] : [];
    },
    [holidaySet]
  );

  // Month view day cell: date line (open count, holiday, bonus, number), then
  // the vacation / absence chips in normal flow — never on top of shifts
  const dayCellContent = useCallback(
    (arg) => {
      // Time-grid columns get no cell content (chips live in the column header)
      if (arg.view.type.startsWith('timeGrid')) return null;
      const dateStr = format(arg.date, 'yyyy-MM-dd');
      const holidayName = holidayNames[dateStr];
      const bonus = sundayBonuses[dateStr];
      const openCount = openByDate[dateStr] || 0;
      const people = peopleByDate[dateStr];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', minWidth: 0, maxWidth: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '3px' }}>
            {openCount > 0 && (
              <span
                title={`${openCount} open ${openCount === 1 ? 'shift' : 'shifts'}`}
                style={{
                  fontSize: '0.62rem', fontWeight: 700, color: '#fff',
                  background: OPEN_STYLE.border, borderRadius: '999px', padding: '0 5px', whiteSpace: 'nowrap',
                }}
              >
                {openCount} open
              </span>
            )}
            {holidayName && (
              <span title={holidayName} style={{ fontSize: '0.65rem', color: 'var(--warning)', fontWeight: 600 }}>✦</span>
            )}
            {bonus && (
              <span
                title={bonus.bonus_type === 'high_50' ? 'Crazy Revenue Sunday — 50%' : 'High Revenue Sunday — 25%'}
                style={{ fontSize: '0.65rem', fontWeight: 700, color: '#E65100', background: '#FFF3E0', borderRadius: '3px', padding: '0 3px' }}
              >
                {bonus.bonus_type === 'high_50' ? '+50%' : '+25%'}
              </span>
            )}
            <span>{arg.dayNumberText}</span>
          </div>
          {people && (
            <DayPeople entry={people} size={isMobile ? 15 : 18} align="flex-end" dateLabel={format(arg.date, 'EEE, d MMM')} />
          )}
        </div>
      );
    },
    [holidayNames, sundayBonuses, peopleByDate, openByDate, isMobile]
  );

  // Week view column header: day + date, then the chips (never over the shifts)
  const dayHeaderContent = useCallback(
    (arg) => {
      if (!arg.view.type.startsWith('timeGrid')) return arg.text;
      const dateStr = format(arg.date, 'yyyy-MM-dd');
      const people = peopleByDate[dateStr];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
          <span>{arg.text}</span>
          {people && <DayPeople entry={people} size={18} align="center" dateLabel={format(arg.date, 'EEE, d MMM')} />}
        </div>
      );
    },
    [peopleByDate]
  );

  // ── Admin: date click → create slot or declare Sunday bonus ─────────────────
  const handleDateClick = useCallback(
    ({ dateStr, date }) => {
      if (!isAdmin) return;
      if (date.getDay() === 0 || holidaySet.has(dateStr)) {
        setSundayBonusModal({ dateStr, existing: sundayBonuses[dateStr] ?? null });
        return;
      }
      const matchingPeriod = periods.find(
        (p) => dateStr >= p.start_date && dateStr <= p.end_date && p.status === 'draft'
      );
      const periodId = matchingPeriod?.id ?? activePeriod?.id ?? null;
      const startTime = format(date, 'HH:mm');
      setSlotModal({ mode: 'create', date: dateStr, startTime, activePeriodId: periodId });
    },
    [isAdmin, periods, activePeriod, sundayBonuses, holidaySet]
  );

  // ── Slot click (shared by desktop calendar and mobile list) ─────────────────
  const handleSlotClick = useCallback(async (slot) => {
    if (!slot) return;
    if (isAdmin) {
      setSlotModal({ mode: 'edit', slot });
    } else if (slot.assigned_user_id === currentUser?.id && slot.period_status === 'published') {
      setShiftTimeEntry(undefined);
      setShiftSicknessRecord(undefined);
      setShiftActionModal(slot);
      const [teRes, srRes] = await Promise.allSettled([
        api.get(`/time-entries/me?shift_slot_id=${slot.id}`),
        api.get('/sickness/me'),
      ]);
      setShiftTimeEntry(
        teRes.status === 'fulfilled' && (teRes.value.data || []).length > 0 ? teRes.value.data[0] : null
      );
      if (srRes.status === 'fulfilled') {
        setShiftSicknessRecord((srRes.value.data || []).find((r) => r.shift_slot_id === slot.id) ?? null);
      } else {
        setShiftSicknessRecord(null);
      }
    } else if (slot.period_status === 'published') {
      // Someone else's (or an open) shift: read-only details with its history
      setSlotModal({ mode: 'edit', slot });
    }
  }, [isAdmin, currentUser?.id]);

  // Link from the Telegram group ("Take it in the app"): /roster?slot=<id>
  const linkedSlot = searchParams.get('slot');
  useEffect(() => {
    if (!linkedSlot) return;
    api.get(`/shifts/slots/${linkedSlot}`)
      .then((r) => {
        calendarRef.current?.getApi()?.gotoDate(r.data.date);
        // Phone week / list: jump to the shift's week
        const start = new Date(`${r.data.date}T00:00:00`);
        const day = start.getDay();
        start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
        setMobileWeekStart(start);
        handleSlotClick(r.data);
      })
      .catch((err) => toast.error(err.response?.data?.error || 'This shift no longer exists.'))
      .finally(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('slot');
        setSearchParams(next, { replace: true });
      });
  }, [linkedSlot]); // eslint-disable-line

  // ── Event click → view / edit slot ──────────────────────────────────────────
  const handleEventClick = useCallback(async ({ event }) => {
    const { slot } = event.extendedProps;
    if (!slot) return; // vacation background events have no slot
    await handleSlotClick(slot);
  }, [handleSlotClick]);

  // ── Create period ────────────────────────────────────────────────────────────
  const handleCreatePeriod = async ({ start_date, end_date }) => {
    setSaving(true);
    try {
      const res = await api.post('/shifts/roster-periods', { start_date, end_date });
      const newPeriod = res.data;
      setPeriods((prev) => [newPeriod, ...prev]);
      setActivePeriod(newPeriod);
      setPeriodModal(false);
      toast.success('Roster plan created.');
      // Navigate calendar to start of new period
      const calApi = calendarRef.current?.getApi();
      if (calApi) calApi.gotoDate(start_date);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create period.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete plan ──────────────────────────────────────────────────────────────
  const handleDeletePeriod = async () => {
    if (!activePeriod) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete this plan?\n\n${activePeriod.start_date} – ${activePeriod.end_date}\n\nThis will remove all shift slots and assignments within it.`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await api.delete(`/shifts/roster-periods/${activePeriod.id}`);
      setPeriods((prev) => prev.filter((p) => p.id !== activePeriod.id));
      const remaining = periods.filter((p) => p.id !== activePeriod.id);
      setActivePeriod(remaining[0] ?? null);
      toast.success('Roster plan deleted.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete plan.');
    } finally {
      setSaving(false);
    }
  };

  const handleForceDelete = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeletingRoster(true);
    try {
      await api.delete(`/shifts/roster-periods/${activePeriod.id}/force`);
      const label = formatPeriodLabel(activePeriod);
      toast.success(`Roster ${label} has been permanently deleted.`);
      setPeriods((prev) => prev.filter((p) => p.id !== activePeriod.id));
      setActivePeriod(null);
      setDeleteRosterModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete roster.');
    } finally {
      setDeletingRoster(false);
    }
  };

  // ── Save slot (create or edit) ───────────────────────────────────────────────
  const handleSaveSlot = async (payload, periodId, flags = {}) => {
    setSaving(true);
    try {
      if (slotModal.mode === 'create') {
        if (!periodId) {
          toast.error('Select a roster period first.');
          return;
        }
        await api.post(`/shifts/roster-periods/${periodId}/slots`, { ...payload, ...flags });
        toast.success('Shift added.');
      } else {
        await api.put(`/shifts/slots/${slotModal.slot.id}`, { ...payload, ...flags });
        toast.success('Shift updated.');
      }
      setSlotModal(null);
      refreshCalendar();
    } catch (err) {
      const extra = confirmServerWarning(err, { verb: 'Save' });
      if (extra) await handleSaveSlot(payload, periodId, { ...flags, ...extra });
      else if (extra === null) toast.error(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to save shift.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete slot ──────────────────────────────────────────────────────────────
  const handleDeleteSlot = async (slotId) => {
    setSaving(true);
    try {
      await api.delete(`/shifts/slots/${slotId}`);
      setSlotModal(null);
      setEvents((prev) => prev.filter((e) => e.id !== String(slotId)));
      toast.success('Shift deleted.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete shift.');
    } finally {
      setSaving(false);
    }
  };

  // ── Publish roster ───────────────────────────────────────────────────────────
  const handlePublish = async () => {
    if (!activePeriod) return;
    const confirmed = window.confirm(
      `Publish the roster for ${activePeriod.start_date} – ${activePeriod.end_date}?\n\nAll active staff will be notified.`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const res = await api.post(`/shifts/roster-periods/${activePeriod.id}/publish`);
      const updated = res.data.period;
      setPeriods((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setActivePeriod(updated);
      refreshCalendar();
      toast.success('Roster published and staff notified.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to publish roster.');
    } finally {
      setSaving(false);
    }
  };

  // ── Add event slots ───────────────────────────────────────────────────────────
  const [addingEventSlots, setAddingEventSlots] = useState(false);

  // Admin-dismissable period warnings (e.g. OM-Meeting overlap conflicts)
  const [periodWarnings, setPeriodWarnings] = useState([]);
  useEffect(() => {
    if (!isAdmin || !activePeriod?.id) { setPeriodWarnings([]); return; }
    let cancelled = false;
    api.get(`/shifts/roster-periods/${activePeriod.id}/warnings`)
      .then((res) => { if (!cancelled) setPeriodWarnings(res.data || []); })
      .catch(() => { if (!cancelled) setPeriodWarnings([]); });
    return () => { cancelled = true; };
  }, [isAdmin, activePeriod?.id]);

  // Staffing check (traffic light) for the selected plan — admin only
  const [staffingCheck, setStaffingCheck] = useState(null);
  const [staffingLoading, setStaffingLoading] = useState(false);
  const [staffingRechecking, setStaffingRechecking] = useState(false);
  useEffect(() => {
    if (!isAdmin || !activePeriod?.id) { setStaffingCheck(null); return; }
    let cancelled = false;
    setStaffingLoading(true);
    api.get(`/shifts/roster-periods/${activePeriod.id}/staffing`)
      .then((res) => { if (!cancelled) setStaffingCheck(res.data || null); })
      .catch(() => { if (!cancelled) setStaffingCheck(null); })
      .finally(() => { if (!cancelled) setStaffingLoading(false); });
    return () => { cancelled = true; };
  }, [isAdmin, activePeriod?.id]);

  const handleRecheckStaffing = async () => {
    if (!activePeriod) return;
    setStaffingRechecking(true);
    try {
      const res = await api.post(`/shifts/roster-periods/${activePeriod.id}/staffing-check`);
      setStaffingCheck(res.data);
      toast.success('Staffing rechecked.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Staffing check failed.');
    } finally {
      setStaffingRechecking(false);
    }
  };

  const handleDismissWarning = async (id) => {
    try {
      await api.post(`/shifts/roster-period-warnings/${id}/dismiss`);
      setPeriodWarnings((prev) => prev.filter((w) => w.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to dismiss warning.');
    }
  };
  const handleAddEventSlots = async () => {
    if (!activePeriod) return;
    setAddingEventSlots(true);
    try {
      const res = await api.post(`/shifts/roster-periods/${activePeriod.id}/add-event-slots`);
      refreshCalendar();
      toast.success(`Event slots added: ${res.data.added} new slot(s).`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add event slots.');
    } finally {
      setAddingEventSlots(false);
    }
  };

  // ── Generate roster ──────────────────────────────────────────────────────────
  // Check for pending vacation first: only approved vacation blocks shifts
  const handleOpenGenerate = async () => {
    if (!activePeriod) return;
    try {
      const res = await api.get(`/shifts/roster-periods/${activePeriod.id}/pending-vacation`);
      setPendingVacation(res.data || []);
    } catch {
      setPendingVacation([]);
    }
    api.get('/shifts/generator-settings').then((r) => setGeneratorSettings(r.data)).catch(() => {});
    setGenerateConfirm(true);
  };

  const handleShowLastReport = async () => {
    if (!activePeriod) return;
    setLoadingReport(true);
    try {
      const res = await api.get(`/shifts/roster-periods/${activePeriod.id}/generation-report`);
      setGenerationReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load the report.');
    } finally {
      setLoadingReport(false);
    }
  };

  // Patch flags of one plan in both the selector list and the active plan
  const updatePeriodFlags = useCallback((id, patch) => {
    setActivePeriod((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
    setPeriods((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  // ── Fill open shifts (fill-only pass) and undo ───────────────────────────────
  const [filling, setFilling] = useState(false);
  const [fillResult, setFillResult] = useState(null);
  const [undoingFill, setUndoingFill] = useState(false);
  // Emergency: let the fill go over the absolute max days in a row (unticked
  // after every run)
  const [fillOverStreak, setFillOverStreak] = useState(false);
  const handleFillOpen = async () => {
    if (!activePeriod) return;
    if (fillOverStreak && !window.confirm(
      'Emergency fill: people may be scheduled above the absolute max days in a row when nobody else can take a shift.\n\n'
      + 'People within the limit always come first. Every such assignment is marked red and recorded in the audit log.\n\nFill anyway?',
    )) return;
    setFilling(true);
    try {
      const res = await api.post(`/shifts/roster-periods/${activePeriod.id}/fill-open`, { allow_streak_override: fillOverStreak });
      setFillOverStreak(false);
      setFillResult(res.data);
      updatePeriodFlags(activePeriod.id, { has_generation_report: true, can_undo_fill: !!res.data.can_undo });
      refreshCalendar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to fill open shifts.');
    } finally {
      setFilling(false);
    }
  };
  const handleUndoFill = async () => {
    if (!activePeriod) return;
    if (!window.confirm('Remove the assignments made by the last "Fill open shifts" run? Assignments changed since then are kept.')) return;
    setUndoingFill(true);
    try {
      const res = await api.post(`/shifts/roster-periods/${activePeriod.id}/fill-open/undo`);
      const { removed, skipped = [] } = res.data;
      toast.success(`Removed ${removed} ${removed === 1 ? 'assignment' : 'assignments'}${skipped.length ? ` · ${skipped.length} kept (changed since)` : ''}.`);
      updatePeriodFlags(activePeriod.id, { can_undo_fill: false });
      setFillResult(null);
      refreshCalendar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to undo the fill.');
    } finally {
      setUndoingFill(false);
    }
  };

  // ── Pins (drafts): count, toggle, unpin all, one-time notice ────────────────
  // The pinned count of the active draft follows every change of the shifts
  const activeDraftId = isAdmin && activePeriod?.status === 'draft' ? activePeriod.id : null;
  useEffect(() => {
    if (!activeDraftId) return undefined;
    let cancelled = false;
    api.get('/shifts/roster-periods').then((res) => {
      const p = (res.data || []).find((x) => x.id === activeDraftId);
      if (!cancelled && p) updatePeriodFlags(activeDraftId, { pinned_count: p.pinned_count });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [events, activeDraftId, updatePeriodFlags]);

  useEffect(() => {
    if (!activeDraftId) { setPinNoticeInfo(null); return undefined; }
    let cancelled = false;
    api.get(`/shifts/roster-periods/${activeDraftId}/pin-notice`)
      .then((res) => { if (!cancelled) setPinNoticeInfo(res.data.notice); })
      .catch(() => { if (!cancelled) setPinNoticeInfo(null); });
    return () => { cancelled = true; };
  }, [activeDraftId]);

  const handleDismissPinNotice = async () => {
    try {
      await api.post(`/shifts/roster-periods/${activeDraftId}/pin-notice/dismiss`);
      setPinNoticeInfo(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to dismiss the notice.');
    }
  };

  const handleTogglePin = async (slot, pinned) => {
    setSaving(true);
    try {
      await api.put(`/shifts/slots/${slot.id}/pin`, { pinned });
      setSlotModal((m) => (m?.slot?.id === slot.id ? { ...m, slot: { ...m.slot, is_pinned: pinned ? 1 : 0 } } : m));
      refreshCalendar();
      toast.success(pinned ? 'Shift pinned — it is kept when you generate again.' : 'Pin removed.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change the pin.');
    } finally {
      setSaving(false);
    }
  };

  const [unpinning, setUnpinning] = useState(false);
  const handleUnpinAll = async () => {
    if (!activePeriod) return;
    const n = activePeriod.pinned_count || 0;
    if (!window.confirm(`Remove the pin from all ${n} pinned shift${n === 1 ? '' : 's'} of this draft?\n\nThe assignments stay as they are, but "Generate" may change them afterwards.`)) return;
    setUnpinning(true);
    try {
      const res = await api.post(`/shifts/roster-periods/${activePeriod.id}/unpin-all`);
      toast.success(`${res.data.unpinned} pin${res.data.unpinned === 1 ? '' : 's'} removed.`);
      setPinNoticeInfo(null);
      refreshCalendar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove the pins.');
    } finally {
      setUnpinning(false);
    }
  };

  // ── "Who could take this?" → one-click assign (same warnings as manual) ─────
  const handleAssignCandidate = async (slot, userId, flags = {}) => {
    setSaving(true);
    try {
      const res = await api.post(`/shifts/slots/${slot.id}/assign`, { user_id: userId, ...flags });
      const name = users.find((u) => u.id === userId)?.name;
      toast.success(name ? `Assigned to ${name}.` : 'Shift assigned.');
      setSlotModal(null);
      refreshCalendar();
      return res.data;
    } catch (err) {
      const extra = confirmServerWarning(err, { verb: 'Assign' });
      if (extra) return handleAssignCandidate(slot, userId, { ...flags, ...extra });
      if (extra === null) toast.error(err.response?.data?.error || 'Failed to assign the shift.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // opts: { levels, variant } — this run only (defaults unchanged)
  const handleGenerate = useCallback(async (opts = {}) => {
    if (!activePeriod) return;
    setGenerating(true);
    setGenerateConfirm(false);
    try {
      const res = await api.post(`/shifts/roster-periods/${activePeriod.id}/generate`, {
        levels: opts.levels,
        variant: opts.variant,
      });
      setGenerationReport(res.data);
      setDiffHighlight(null);
      updatePeriodFlags(activePeriod.id, { has_generation_report: true, can_undo_fill: false });
      refreshCalendar();
      toast.success(
        `Generated: ${res.data.summary.filled_slots}/${res.data.summary.total_slots} shifts filled.`
      );
    } catch (err) {
      toast.error(err.response?.data?.error || 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  }, [activePeriod, refreshCalendar, updatePeriodFlags]);

  // ── Sunday bonus handlers ─────────────────────────────────────────────────────
  const handleBonusSave = async (dateStr, bonusType, existingId) => {
    setBonusSaving(true);
    try {
      if (existingId) {
        await api.put(`/sunday-bonus/${existingId}`, { bonus_type: bonusType });
      } else {
        await api.post('/sunday-bonus', { date: dateStr, bonus_type: bonusType });
      }
      toast.success('Sunday bonus updated.');
      setSundayBonusModal(null);
      if (currentRangeRef.current) loadSundayBonuses(currentRangeRef.current.start, currentRangeRef.current.end);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save bonus.');
    } finally {
      setBonusSaving(false);
    }
  };

  const handleBonusRemove = async (id) => {
    setBonusSaving(true);
    try {
      await api.delete(`/sunday-bonus/${id}`);
      toast.success('Sunday bonus removed.');
      setSundayBonusModal(null);
      if (currentRangeRef.current) loadSundayBonuses(currentRangeRef.current.start, currentRangeRef.current.end);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove bonus.');
    } finally {
      setBonusSaving(false);
    }
  };

  // ── Clock in/out handlers ─────────────────────────────────────────────────────
  const handleClockSave = async (slot, existingEntry, clockedIn, clockedOut) => {
    setClockSaving(true);
    try {
      if (existingEntry) {
        await api.put(`/time-entries/${existingEntry.id}`, { clocked_in_at: clockedIn, clocked_out_at: clockedOut });
      } else {
        await api.post('/time-entries', { shift_slot_id: slot.id, clocked_in_at: clockedIn, clocked_out_at: clockedOut });
      }
      toast.success('Time entry saved.');
      setClockModal(null);
      refreshCalendar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save time entry.');
    } finally {
      setClockSaving(false);
    }
  };

  // Sick note set / removed in the shift modal: keep the modal open with the new state
  const handleAuChange = (record) => setShiftSicknessRecord(record);

  const handleCreateCoverRequest = useCallback(async (slot, actionType) => {
    setCoverSaving(true);
    setCoverConfirm(null);
    try {
      if (actionType === 'sick') {
        await api.post('/sickness', { shift_slot_id: slot.id });
        toast.success('Sick leave reported. Team has been notified.');
      } else {
        await api.post('/shift-covers', { shift_slot_id: slot.id });
        toast.success('Cover request created. Team has been notified.');
      }
      await loadCovers();
      refreshCalendar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Request failed.');
    } finally {
      setCoverSaving(false);
    }
  }, [loadCovers, refreshCalendar]);

  const handleTakeShift = useCallback(async (coverId, flags = {}) => {
    setCoverSaving(true);
    try {
      await api.post(`/shift-covers/${coverId}/take`, flags);
      toast.success('Shift taken. Waiting for confirmation if needed.');
      await loadCovers();
      refreshCalendar();
    } catch (err) {
      const extra = confirmServerWarning(err, { verb: 'Take this shift', self: true, notifiesAdmins: true });
      if (extra) await handleTakeShift(coverId, { ...flags, ...extra });
      else if (extra === null) toast.error(err.response?.data?.error || 'Could not take shift.');
      // Expired (shift started) or arranged by an admin meanwhile: refresh the lists
      if (err.response?.data?.expired || err.response?.data?.resolved) { loadCovers(); refreshCalendar(); }
    } finally {
      setCoverSaving(false);
    }
  }, [loadCovers, refreshCalendar]);

  const handleOfferSwap = useCallback(async (cover) => {
    // Load my shifts and open the swap picker
    try {
      const res = await api.get('/shifts/my-shifts');
      setMyShiftsForSwap(res.data);
      setSwapPickerModal({ cover });
    } catch {
      toast.error('Could not load your shifts.');
    }
  }, []);

  const handleSubmitSwapOffer = useCallback(async (coverId, offeredSlotId, flags = {}) => {
    setCoverSaving(true);
    setSwapPickerModal(null);
    try {
      await api.post(`/shift-covers/${coverId}/offer-swap`, { offered_slot_id: offeredSlotId, ...flags });
      toast.success('Swap offer sent. Waiting for their confirmation.');
      await loadCovers();
    } catch (err) {
      const extra = confirmServerWarning(err, { verb: 'Offer this swap', self: true, notifiesAdmins: true });
      if (extra) await handleSubmitSwapOffer(coverId, offeredSlotId, { ...flags, ...extra });
      else if (extra === null) toast.error(err.response?.data?.error || 'Offer failed.');
    } finally {
      setCoverSaving(false);
    }
  }, [loadCovers]);

  const handleConfirmCover = useCallback(async (coverId, flags = {}) => {
    setCoverSaving(true);
    try {
      await api.post(`/shift-covers/${coverId}/confirm`, flags);
      toast.success('Cover confirmed. Roster updated.');
      await loadCovers();
      refreshCalendar();
    } catch (err) {
      const extra = confirmServerWarning(err, { verb: 'Confirm', notifiesAdmins: true });
      if (extra) await handleConfirmCover(coverId, { ...flags, ...extra });
      else if (extra === null) toast.error(err.response?.data?.error || 'Confirmation failed.');
    } finally {
      setCoverSaving(false);
    }
  }, [loadCovers, refreshCalendar]);

  const handleCancelCover = useCallback(async (coverId) => {
    setCoverSaving(true);
    try {
      await api.post(`/shift-covers/${coverId}/cancel`);
      toast.success('Cover request cancelled.');
      await loadCovers();
      refreshCalendar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not cancel request.');
    } finally {
      setCoverSaving(false);
    }
  }, [loadCovers, refreshCalendar]);

  // ── Period selector change ────────────────────────────────────────────────────
  const handlePeriodChange = (e) => {
    const id = Number(e.target.value);
    const p = periods.find((p) => p.id === id) ?? null;
    setActivePeriod(p);
    if (p) {
      if (isMobile && mobileView !== 'calendar') {
        const start = new Date(p.start_date + 'T00:00:00');
        const day = start.getDay();
        start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
        start.setHours(0, 0, 0, 0);
        setMobileWeekStart(start);
      } else {
        const calApi = calendarRef.current?.getApi();
        if (calApi) calApi.gotoDate(p.start_date);
      }
    }
  };

  // ── Next open shift: first date with an open slot after the visible range ────
  const usesMobileWeek = isMobile && mobileView !== 'calendar';
  const goToNextOpen = () => {
    const dates = [...new Set((planOpenSlots || []).map((s) => s.date))].sort();
    if (!dates.length) return;
    let visStart;
    let visEnd; // exclusive
    if (usesMobileWeek) {
      visStart = format(mobileWeekStart, 'yyyy-MM-dd');
      visEnd = addDaysStr(visStart, 7);
    } else {
      const view = calendarRef.current?.getApi()?.view;
      visStart = view ? format(view.currentStart, 'yyyy-MM-dd') : '0000-00-00';
      visEnd = view ? format(view.currentEnd, 'yyyy-MM-dd') : '0000-00-00';
    }
    // Next one after this view; at the end, wrap to the first one before it
    const target = dates.find((d) => d >= visEnd) || dates.find((d) => d < visStart);
    if (!target) {
      toast('All open shifts of this plan are in the current view.');
      return;
    }
    if (usesMobileWeek) {
      const d = new Date(target + 'T00:00:00');
      const day = d.getDay();
      d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      d.setHours(0, 0, 0, 0);
      setMobileWeekStart(d);
    } else {
      calendarRef.current?.getApi()?.gotoDate(target);
    }
  };

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Roster</h1>
          <p className="page-subtitle">
            {isAdmin ? 'Plan and publish shift assignments.' : 'View the published shift schedule.'}
          </p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setPeriodModal(true)}>
            <Plus size={15} />
            New Plan
          </button>
        )}
      </div>

      {/* Admin toolbar */}
      {isAdmin && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '16px',
            flexWrap: 'wrap',
          }}
        >
          {/* Period selector */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <select
              className="form-select"
              style={{ paddingRight: '32px', minWidth: '220px' }}
              value={activePeriod?.id ?? ''}
              onChange={handlePeriodChange}
            >
              <option value="">— Select plan —</option>
              {periods.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.start_date} – {p.end_date} ({p.status})
                </option>
              ))}
            </select>
            <ChevronDown
              size={14}
              style={{
                position: 'absolute',
                right: '10px',
                pointerEvents: 'none',
                color: 'var(--text-muted)',
              }}
            />
          </div>

          {/* Person selector: review the plan person by person */}
          {activePeriod && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: 'var(--text-light)' }}>
              <span style={{ whiteSpace: 'nowrap' }}>Show plan for</span>
              <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                {selectedUserId && (
                  <ColorDot userId={selectedUserId} size={10} style={{ position: 'absolute', left: '11px', pointerEvents: 'none' }} />
                )}
                <select
                  className="form-select"
                  style={{ paddingRight: '32px', minWidth: '170px', ...(selectedUserId ? { paddingLeft: '28px' } : {}) }}
                  value={selectedUserId ?? ''}
                  onChange={(e) => setSelectedUserId(Number(e.target.value) || null)}
                >
                  <option value="">All staff</option>
                  {personOptions.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                  {selectedUserId && !personOptions.some((u) => u.id === selectedUserId) && (
                    <option value={selectedUserId}>User #{selectedUserId}</option>
                  )}
                </select>
                <ChevronDown size={14} style={{ position: 'absolute', right: '10px', pointerEvents: 'none', color: 'var(--text-muted)' }} />
              </span>
            </label>
          )}

          {activePeriod && <PeriodBadge status={activePeriod.status} />}

          {/* Open-slot counter: click toggles "only open shifts" */}
          {activePeriod && openCounts && (openCounts.total > 0 ? (
            <>
              <button
                type="button"
                onClick={() => setOpenOnly((v) => !v)}
                disabled={!!selectedUserId}
                aria-pressed={openOnly}
                title={`${openCounts.regular} open regular ${openCounts.regular === 1 ? 'shift' : 'shifts'} · ${openCounts.events} open event ${openCounts.events === 1 ? 'slot' : 'slots'}\n${selectedUserId ? 'Filter not available while one person is shown' : `Click: ${openOnly ? 'show all shifts' : 'show only open shifts'}`}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px', minHeight: '32px',
                  padding: '4px 10px', borderRadius: '999px', cursor: selectedUserId ? 'not-allowed' : 'pointer', fontSize: '0.8rem', fontWeight: 700,
                  opacity: selectedUserId ? 0.55 : 1,
                  border: `1.5px solid ${OPEN_STYLE.border}`,
                  background: openOnly ? OPEN_STYLE.border : OPEN_STYLE.bg,
                  color: openOnly ? '#fff' : OPEN_STYLE.text,
                }}
              >
                <AlertTriangle size={13} aria-hidden="true" />
                {openCounts.total} open {openCounts.total === 1 ? 'shift' : 'shifts'}
                {openOnly && <span style={{ fontWeight: 500 }}>· showing only open</span>}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={goToNextOpen}
                disabled={!!selectedUserId}
                title="Jump to the next date with an open shift"
              >
                Next open shift <ArrowRight size={13} />
              </button>
            </>
          ) : (
            <span
              title="0 open regular shifts · 0 open event slots"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px', minHeight: '32px',
                padding: '4px 10px', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 700,
                border: '1.5px solid var(--success)', background: 'var(--success-light)', color: 'var(--success)',
              }}
            >
              <CheckCircle size={13} aria-hidden="true" />
              All shifts filled
            </span>
          ))}

          {activePeriod && isAdmin && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate('/availability')}
            >
              Collect Availabilities →
            </button>
          )}

          {activePeriod?.status === 'draft' && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleOpenGenerate}
              disabled={generating || saving}
            >
              <Wand2 size={14} />
              {generating ? 'Generating…' : 'Generate Roster'}
            </button>
          )}

          {activePeriod?.status === 'draft' && activePeriod?.has_generation_report && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleShowLastReport}
              disabled={loadingReport}
              title="Show the report of the last generation (reflects the current plan)"
            >
              <FileCheck size={14} />
              {loadingReport ? 'Loading…' : 'Last report'}
            </button>
          )}

          {activePeriod?.status === 'draft' && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleFillOpen}
              disabled={filling || generating || saving || (openCounts && openCounts.total === 0)}
              title="Fill only the open shifts — existing assignments are never changed. Uses agreed extra shifts first."
            >
              <UserPlus size={14} />
              {filling ? 'Filling…' : 'Fill open shifts'}
            </button>
          )}

          {activePeriod?.status === 'draft' && (
            <label
              title="Only for emergencies: lets 'Fill open shifts' go over the absolute max days in a row when nobody else can take a shift"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', cursor: 'pointer', minHeight: '32px',
                padding: '2px 8px', borderRadius: 'var(--radius)',
                color: fillOverStreak ? 'var(--danger)' : 'var(--text-light)',
                border: `1px solid ${fillOverStreak ? 'var(--danger)' : 'transparent'}`,
                background: fillOverStreak ? 'var(--danger-light)' : 'transparent',
              }}
            >
              <input
                type="checkbox"
                checked={fillOverStreak}
                onChange={(e) => setFillOverStreak(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: 'var(--danger)' }}
              />
              Emergency: allow exceeding the max days in a row
            </label>
          )}

          {activePeriod?.status === 'draft' && activePeriod?.pinned_count > 0 && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleUnpinAll}
              disabled={unpinning || generating || saving}
              title="Remove all pins of this draft — the assignments stay as they are"
            >
              <Pin size={14} />
              {unpinning ? 'Unpinning…' : `Unpin all (${activePeriod.pinned_count})`}
            </button>
          )}

          {isAdmin && activePeriod && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setVersionsOpen(true)}
              title="Saved versions of this draft: compare and restore"
            >
              <History size={14} />
              Versions
            </button>
          )}

          {diffHighlight && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setDiffHighlight(null)}
              title="Hide the highlight of the differing shifts"
              style={{ borderColor: '#E65100', color: '#E65100' }}
            >
              <XIcon size={13} />
              {diffHighlight.size} differing shift{diffHighlight.size === 1 ? '' : 's'} highlighted
            </button>
          )}

          {activePeriod?.status === 'draft' && activePeriod?.can_undo_fill && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleUndoFill}
              disabled={undoingFill || filling || saving}
              title="Remove the assignments made by the last fill run (unless changed since)"
            >
              <Undo2 size={14} />
              {undoingFill ? 'Undoing…' : 'Undo last fill'}
            </button>
          )}

          {activePeriod?.status === 'draft' && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleAddEventSlots}
              disabled={addingEventSlots || saving}
              title="Add event slots for any active special events not yet in this plan"
            >
              🧹 {addingEventSlots ? 'Adding…' : 'Add Event Slots'}
            </button>
          )}

          {activePeriod?.status === 'draft' && (
            <button
              className="btn btn-success btn-sm"
              onClick={handlePublish}
              disabled={saving}
            >
              <Send size={13} />
              Publish Roster
            </button>
          )}

          {activePeriod?.status === 'draft' && (
            <button
              className="btn btn-danger btn-sm"
              onClick={handleDeletePeriod}
              disabled={saving}
            >
              <Trash2 size={13} />
              Delete Plan
            </button>
          )}

          {activePeriod?.status === 'published' && !isSuperAdmin && (
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Published plans cannot be deleted.
            </span>
          )}

          {activePeriod && isSuperAdmin && (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => { setDeleteConfirmText(''); setDeleteRosterModal(true); }}
              disabled={saving}
            >
              <Trash2 size={13} />
              Delete Roster
            </button>
          )}
        </div>
      )}

      {/* Period warnings (e.g. OM-Meeting overlap conflicts) — admin only, dismissable */}
      {isAdmin && periodWarnings.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
          {periodWarnings.map((w) => (
            <div
              key={w.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 14px',
                background: 'var(--warning-light)',
                border: '1px solid var(--warning)',
                borderRadius: 'var(--radius)',
                fontSize: '0.85rem',
              }}
            >
              <span style={{ flexShrink: 0 }}>⚠️</span>
              <span style={{ flex: 1 }}>{w.message}</span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleDismissWarning(w.id)}
                title="Dismiss"
                aria-label="Dismiss warning"
              >
                <XIcon size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* One-time notice: shifts pinned automatically by the update (per admin) */}
      {isAdmin && activePeriod?.status === 'draft' && pinNoticeInfo && (
        <div
          role="status"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 14px', marginBottom: '16px',
            background: 'var(--primary-xlight)', border: '1px solid var(--primary)', borderRadius: 'var(--radius)', fontSize: '0.85rem',
          }}
        >
          <span style={{ flexShrink: 0 }}>📌</span>
          <span style={{ flex: 1, lineHeight: 1.5 }}>
            {pinNoticeInfo.pinned_count} shift{pinNoticeInfo.pinned_count === 1 ? ' was' : 's were'} pinned automatically because
            {pinNoticeInfo.pinned_count === 1 ? ' it was' : ' they were'} changed by hand after the last generation – please
            check (📌). You can unpin them individually or with &quot;Unpin all&quot;.
          </span>
          <button className="btn btn-secondary btn-sm" onClick={handleDismissPinNotice} title="Dismiss" aria-label="Dismiss notice">
            <XIcon size={13} />
          </button>
        </div>
      )}

      {/* How the published plan was made (frozen at publish) — admin only */}
      {isAdmin && activePeriod?.status === 'published' && <PublishSnapshot periodId={activePeriod.id} />}

      {/* Staffing check (traffic light) — admin only */}
      {isAdmin && activePeriod && (
        <StaffingCard
          check={staffingCheck}
          loading={staffingLoading}
          rechecking={staffingRechecking}
          onRecheck={handleRecheckStaffing}
        />
      )}

      {/* Per-person review: monthly summary or team overview — admin only */}
      {isAdmin && activePeriod && (selectedUserId ? (
        <PersonSummaryCard summary={summaryForPlan} userId={selectedUserId} loading={userSummaryLoading} periodId={activePeriod.id} onChanged={reloadSummary} />
      ) : (
        <TeamOverviewCard summary={summaryForPlan} loading={userSummaryLoading} onSelectUser={setSelectedUserId} periodId={activePeriod.id} onChanged={reloadSummary} />
      ))}

      {/* No periods hint */}
      {isAdmin && periods.length === 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            background: 'var(--primary-xlight)',
            borderRadius: 'var(--radius)',
            border: '1px solid var(--primary-light)',
            marginBottom: '16px',
            fontSize: '0.875rem',
            color: 'var(--text-light)',
          }}
        >
          <Info size={15} style={{ flexShrink: 0, color: 'var(--primary-dark)' }} />
          Create a roster plan to start adding shifts to the calendar.
        </div>
      )}

      {/* Legend — hidden on mobile in list/week mode (rows are self-explanatory) */}
      {(!isMobile || mobileView === 'calendar') && (
        <div
          style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '12px',
            fontSize: '0.78rem',
            color: 'var(--text-muted)',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {loading && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
              Loading…
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }} title="Each shift is shown in the assigned person's colour">
            <span style={{ width: 14, height: 14, borderRadius: 3, boxSizing: 'border-box', display: 'inline-block', ...lookStyle(shiftLook(LEGEND_SLOT, LEGEND_COLOUR)) }} />
            Assigned (person's colour)
          </span>
          {isAdmin && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: 14, height: 14, borderRadius: 3, boxSizing: 'border-box', display: 'inline-block', ...lookStyle(shiftLook({ ...LEGEND_SLOT, period_status: 'draft' }, LEGEND_COLOUR)) }} />
              Draft
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, boxSizing: 'border-box', display: 'inline-block', ...lookStyle(shiftLook({}, null)) }} />
            Open shift
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ ...CHIP_STYLE, fontSize: '0.68rem', color: '#C2410C' }}>↔</span>
            Swap wanted
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ ...CHIP_STYLE, fontSize: '0.68rem' }}>🏥</span>
            Sick cover
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#E65100', background: '#FFF3E0', borderRadius: '3px', padding: '0 3px' }}>+25%</span>
            Sunday bonus
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <TrainingChip size={11} />
            Training shift
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--primary-dark)', color: '#fff', fontSize: '0.55rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>AB</span>
            On vacation
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ width: 16, height: 16, borderRadius: '50%', boxSizing: 'border-box', background: '#F1F5F9', border: '1.5px dashed #64748B', color: '#475569', fontSize: '0.55rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>AB</span>
            Absent
          </span>
        </div>
      )}

      {/* Mobile view tabs */}
      {isMobile && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
          {MOBILE_VIEWS.map((v) => (
            <button
              key={v}
              onClick={() => changeMobileView(v)}
              style={{
                flex: 1, padding: '9px 0',
                border: `1px solid ${mobileView === v ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: 'var(--radius)',
                background: mobileView === v ? 'var(--primary)' : 'var(--white)',
                color: mobileView === v ? '#fff' : 'var(--text)',
                fontWeight: mobileView === v ? 700 : 400,
                fontSize: '0.9rem', cursor: 'pointer',
              }}
            >
              {v === 'week' ? 'Week' : v === 'list' ? 'List' : 'Month'}
            </button>
          ))}
        </div>
      )}

      {/* Mobile week table */}
      {isMobile && mobileView === 'week' && (
        <MobileWeekTable
          events={visibleEvents}
          weekStart={mobileWeekStart}
          onPrevWeek={goMobilePrevWeek}
          onNextWeek={goMobileNextWeek}
          onToday={goMobileToday}
          currentUser={currentUser}
          isAdmin={isAdmin}
          holidaySet={holidaySet}
          holidayNames={holidayNames}
          sundayBonuses={sundayBonuses}
          peopleByDate={peopleByDate}
          onSlotClick={handleSlotClick}
          loading={loading}
        />
      )}

      {/* Mobile list view */}
      {isMobile && mobileView === 'list' && (
        <MobileRosterView
          events={visibleEvents}
          weekStart={mobileWeekStart}
          onPrevWeek={goMobilePrevWeek}
          onNextWeek={goMobileNextWeek}
          filter={selectedUserId ? 'all' : mobileFilter}
          onFilterChange={setMobileFilter}
          hideFilter={!!selectedUserId}
          currentUser={currentUser}
          isAdmin={isAdmin}
          holidaySet={holidaySet}
          holidayNames={holidayNames}
          sundayBonuses={sundayBonuses}
          peopleByDate={peopleByDate}
          onSlotClick={handleSlotClick}
          loading={loading}
        />
      )}

      {/* Desktop calendar (always) or the phone Month tab */}
      {(!isMobile || mobileView === 'calendar') && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
            initialView={isMobile ? 'dayGridMonth' : 'timeGridWeek'}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: isMobile ? '' : 'timeGridWeek,dayGridMonth', // phone: the Month tab above does this
            }}
            buttonText={{ today: 'Today', week: 'Week', month: 'Month' }}
            // Same start: shift-type display order, then longer first (FullCalendar's default)
            eventOrder="start,typeRank,-duration,title"
            eventOrderStrict
            slotMinTime="07:00:00"
            slotMaxTime="24:00:00"
            allDaySlot={false}
            weekends
            firstDay={1}
            locale="en-GB"
            height="auto"
            contentHeight={600}
            events={visibleEvents}
            datesSet={handleDatesSet}
            dateClick={isAdmin ? handleDateClick : undefined}
            eventClick={handleEventClick}
            dayCellClassNames={dayCellClassNames}
            dayCellContent={dayCellContent}
            dayHeaderContent={dayHeaderContent}
            eventContent={renderEventContent}
            eventMinHeight={28}
            nowIndicator
            slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          />
        </div>
      )}

      {/* Modals */}
      <CreatePeriodModal
        isOpen={periodModal}
        onClose={() => setPeriodModal(false)}
        onSave={handleCreatePeriod}
        saving={saving}
      />

      <SlotModal
        isOpen={!!slotModal}
        onClose={() => setSlotModal(null)}
        mode={slotModal?.mode}
        slot={slotModal?.slot}
        date={slotModal?.date}
        startTime={slotModal?.startTime}
        activePeriodId={slotModal?.activePeriodId}
        periods={periods}
        users={users}
        templates={templates}
        onSave={handleSaveSlot}
        onAssignCandidate={isAdmin ? handleAssignCandidate : undefined}
        onTogglePin={isAdmin ? handleTogglePin : undefined}
        onDelete={handleDeleteSlot}
        saving={saving || coverSaving}
        isAdmin={isAdmin}
        openCover={slotModal?.slot ? openCovers.find((c) => c.slot_id === slotModal.slot.id) : null}
        onTakeCover={async (coverId) => { await handleTakeShift(coverId); setSlotModal(null); }}
        onOfferSwap={(cover) => { setSlotModal(null); handleOfferSwap(cover); }}
        onTookOpen={() => { setSlotModal(null); refreshCalendar(); loadCovers(); }}
      />

      {versionsOpen && activePeriod && (
        <DraftVersionsModal
          period={activePeriod}
          onClose={() => setVersionsOpen(false)}
          onRestored={() => { refreshCalendar(); reloadSummary?.(); }}
          onHighlight={(ids) => setDiffHighlight(new Set(ids))}
        />
      )}

      {/* Generate Confirm Modal */}
      <GenerateConfirmModal
        isOpen={generateConfirm}
        onClose={() => setGenerateConfirm(false)}
        onGenerate={handleGenerate}
        generating={generating}
        activePeriod={activePeriod}
        pendingVacation={pendingVacation}
        generatorSettings={generatorSettings}
        onSettingsChanged={() => api.get('/shifts/generator-settings').then((r) => setGeneratorSettings(r.data)).catch(() => {})}
        onUnpinAll={handleUnpinAll}
      />

      {/* Generation Report Modal */}
      {generationReport && (
        <GenerationReportModal
          report={generationReport}
          onClose={() => setGenerationReport(null)}
        />
      )}

      {fillResult && (
        <FillResultModal
          result={fillResult}
          onClose={() => setFillResult(null)}
          onUndo={activePeriod?.can_undo_fill ? handleUndoFill : undefined}
          undoing={undoingFill}
        />
      )}

      {/* Shift action modal (own shift click) */}
      <ShiftActionModal
        slot={shiftActionModal}
        onClose={() => setShiftActionModal(null)}
        onSwap={(slot) => setCoverConfirm({ slot, actionType: 'swap' })}
        onSick={(slot) => setCoverConfirm({ slot, actionType: 'sick' })}
        onClock={(slot, te) => { setClockModal({ slot, timeEntry: te }); setShiftActionModal(null); }}
        timeEntry={shiftTimeEntry}
        sicknessRecord={shiftSicknessRecord}
        onAu={handleAuChange}
      />

      {/* Clock modal */}
      {clockModal && (
        <ClockModal
          slot={clockModal.slot}
          timeEntry={clockModal.timeEntry}
          onClose={() => setClockModal(null)}
          onSave={handleClockSave}
          saving={clockSaving}
        />
      )}

      {/* Sunday bonus modal (admin only) */}
      {isAdmin && (
        <SundayBonusModal
          data={sundayBonusModal}
          onClose={() => setSundayBonusModal(null)}
          onSave={handleBonusSave}
          onRemove={handleBonusRemove}
          saving={bonusSaving}
        />
      )}

      {/* Cover confirm modal */}
      <CoverConfirmModal
        data={coverConfirm}
        onClose={() => setCoverConfirm(null)}
        onConfirm={handleCreateCoverRequest}
        saving={coverSaving}
      />

      {/* Swap picker modal */}
      <SwapPickerModal
        data={swapPickerModal}
        myShifts={myShiftsForSwap}
        onClose={() => setSwapPickerModal(null)}
        onOffer={handleSubmitSwapOffer}
        saving={coverSaving}
      />

      {/* Open covers panel (below calendar) */}
      <OpenCoversPanel
        covers={openCovers}
        onTake={handleTakeShift}
        onOfferSwap={handleOfferSwap}
        saving={coverSaving}
      />

      {/* My cover activity panel */}
      <MyCoversPanel
        myCovers={myCovers}
        onConfirm={handleConfirmCover}
        onCancel={handleCancelCover}
        saving={coverSaving}
      />

      {/* Force-delete roster modal (superadmin only) */}
      {deleteRosterModal && activePeriod && (
        <div className="modal-overlay" onClick={() => setDeleteRosterModal(false)}>
          <div className="modal" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Delete this roster permanently?</h2>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '12px' }}>
                This will permanently delete the{' '}
                <strong>{formatPeriodLabel(activePeriod)}</strong> roster and all associated data
                including:
              </p>
              <ul style={{ paddingLeft: '20px', marginBottom: '12px', lineHeight: 1.8 }}>
                <li>All shift assignments for this period</li>
                <li>All availability submissions for this period</li>
                <li>All shift swap and sick leave records for this period</li>
              </ul>
              <p style={{ marginBottom: '16px' }}>
                <strong>This cannot be undone.</strong>
              </p>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '16px' }}>
                Note: time entries that have already been included in a generated monthly report
                will not be deleted, as they are part of payroll records.
              </p>
              <label
                style={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', marginBottom: '6px' }}
              >
                Type DELETE to confirm
              </label>
              <input
                className="form-input"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteRosterModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                disabled={deleteConfirmText !== 'DELETE' || deletingRoster}
                onClick={handleForceDelete}
              >
                {deletingRoster ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RosterPage;
